import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import CommaConvert from "../../../utils/CommaConvert";
import { formatAmountComma } from "../../../utils/decimalAmountUtils";
import { useCurrencySymbol } from "../../../utils/currencyUtils";
import DateConversion from "../../../utils/DateConversion";
import { fetchHardwareDescriptions } from "../../../store/slices/hardwareDescriptionSlice";
import { fetchWorkEntries } from "../../../store/slices/workentriesSlice";
import { fetchEmployees } from "../../../store/slices/employeeSlice";
import { fetchDescriptionTasks } from "../../../store/slices/DescriptionTask";
import { fetchAllProjects } from "../../../store/slices/projectsSlice";
import { fetchSalesTaxes } from "../../../store/slices/salesTaxesSlice";
import { cancelManagerFromProjects } from "../../../store/slices/cancelManagerSlice";
import { salespersonRejectProject } from "../../../store/slices/SalesPersonProjectRejectSlice";
import AdminLoader from "../../../components/common/loader/AdminLoader";
import QuotedExpenseTable from "../../Operations/SalesOperations/SaleOperation/ResponseReceived/QuotedExpenseTable";
import { fetchLeads } from "../../../store/slices/leadsSlice";
import useMatchedProject from "./useMatchedProject";
import useMatchedOperationsData from "./useMatchedOperationsData";
import { goNext, goToStep, setCreatedProject, setOperationProjectStatus } from "../../../store/slices/WizardSlice/wizardStepSlice";
import { setCurrentPageTitle } from "../../../store/slices/pageSlice";
import { salespersonAcceptAction } from "../../../store/slices/salespersonAcceptAction";
import { fetchSalesHourlyRates } from "../../../store/slices/salesHourlyRateSlice";
import useSubscriptionLevel from "../../../utils/SubscriptionLevel";
import { fetchGroups } from "../../../store/slices/groupSlice";
import { fetchOtherExpenses } from "../../../store/slices/otherexpenses";
import {
    getLoggedSalesUserId,
    getPerdiemAndAverageCostFromExpenses,
    scopeOtherExpensesForUser,
} from "../../../utils/salespersonRatesScope";

/** Other Expenses column: line items only (excludes installation per diem / average cost). */
const sumOtherExpensesLineItemsOnly = (descriptions) => {
    if (!Array.isArray(descriptions)) return 0;
    return descriptions.reduce(
        (sum, desc) => sum + (Number(desc?.value) || 0),
        0
    );
};

/** Normalize group field for composite keys (QA may return populated object; localhost often has plain string). */
function normalizeAssignedGroupForKey(v) {
    if (v == null || v === "") return "";
    if (typeof v === "object" && v !== null) {
        return String(
            v._id ??
            v.id ??
            v.newGroupName ??
            v.groupname ??
            v.GroupName ??
            v.name ??
            ""
        ).trim();
    }
    return String(v).trim();
}

/** Same resolution order as SalesProjects Response Received: quotationId first, else name composite. */
function projectResponseReceivedCompositeKey(project) {
    const q = project?.quotationId;
    if (q != null && String(q).trim() !== "") {
        return `q:${String(q).trim()}`;
    }
    const ag = normalizeAssignedGroupForKey(project?.AssignedGroup);
    return `${(project?.ProjectName || "").trim()}||${(project?.ClientName || "").trim()}||${ag}`.toLowerCase();
}

function leadResponseReceivedCompositeKey(lead) {
    const q = lead?.quotationId;
    if (q != null && String(q).trim() !== "") {
        return `q:${String(q).trim()}`;
    }
    const ag = normalizeAssignedGroupForKey(lead?.AssignedGroup);
    return `${(lead?.ProjectName || "").trim()}||${(lead?.ClientName || "").trim()}||${ag}`.toLowerCase();
}

function groupRowCompositeKeyForLeadMatch(group) {
    const p0 = group?.Projects?.[0];
    return projectResponseReceivedCompositeKey({
        quotationId: p0?.quotationId,
        ProjectName: group?.ProjectName ?? p0?.ProjectName,
        ClientName: group?.ClientName ?? p0?.ClientName,
        AssignedGroup: group?.AssignedGroup ?? p0?.AssignedGroup,
    });
}

function isSalespersonResponseReceived(person) {
    return (
        String(person?.salespersonstatus ?? "")
            .trim()
            .toLowerCase() === "response received"
    );
}

function isProjectOutstandingCleared(person) {
    return (
        person?.isProjectoutstanding === false ||
        person?.isProjectoutstanding === "false" ||
        person?.isProjectoutstanding === 0
    );
}

const ResponseReceivedWizard = ({ selectedLeadGroupId = "" }) => {

    const dispatch = useDispatch();
    const USAComma = CommaConvert();
    const { formatDateByCountry } = DateConversion();
    const { enqueueSnackbar } = useSnackbar();
    const currency = useCurrencySymbol();
    const subscriptionPackage = useSubscriptionLevel();
    const { employees } = useSelector((state) => state.employees);
    const { tasks } = useSelector((state) => state.descriptionTasks);
    const { entries } = useSelector((state) => state.workentries);
    const { items } = useSelector((state) => state.hardwareDescription);
    const { leads } = useSelector((state) => state.leads);
    const { projects } = useSelector((state) => state.projects);
    const { data } = useSelector((state) => state.salesHourlyRate);
    const { items: otherExpenses } = useSelector((state) => state.otherExpenses);
    const loggedInUserId = useSelector((state) => state.leads.loggedInUserId);
    const plEmail =
        typeof localStorage !== "undefined" ? localStorage.getItem("email") : "";
    const loggedUserIdForExpenses = React.useMemo(
        () => getLoggedSalesUserId(loggedInUserId, employees, plEmail),
        [loggedInUserId, employees, plEmail]
    );
    const { group: groups } = useSelector((state) => state.group);
    const createdProject = useSelector((state) => state.wizardstep.createdProject);
    const operationProjectName = createdProject[0]?.ProjectName
    const selectedLeadQuotationId = createdProject[0]?.quotationId || createdProject[0]?.id || null; // Get unique identifier from selected lead

    useEffect(() => {
        dispatch(setCurrentPageTitle("Response Received"));
        dispatch(fetchHardwareDescriptions());
        dispatch(fetchWorkEntries());
        dispatch(fetchEmployees());
        dispatch(fetchDescriptionTasks());
        dispatch(fetchAllProjects());
        dispatch(fetchSalesTaxes());
        dispatch(fetchLeads());
        dispatch(fetchSalesHourlyRates());
        dispatch(fetchOtherExpenses());
        dispatch(fetchGroups());
    }, [dispatch]);

    const groupNameById = React.useMemo(() => {
        const map = new Map();
        (groups || []).forEach((g) => {
            const id = g?._id || g?.id;
            const name = g?.newGroupName || g?.groupname || g?.groupName || g?.name || "";
            if (id && name) map.set(String(id), name);
        });
        return map;
    }, [groups]);

    const getDisplayGroupName = useCallback((item) => {
        const groupId = item?.groupid?._id || item?.groupid || item?.Projects?.[0]?.groupid?._id || item?.Projects?.[0]?.groupid || selectedLeadGroupId;
        if (groupId && groupNameById.has(String(groupId))) {
            return groupNameById.get(String(groupId));
        }
        return item?.AssignedGroup || "";
    }, [groupNameById, selectedLeadGroupId]);

    const [projectsReceived, setProjectsReceived] = useState([]);
    const [responseReceivedState, setResponseReceivedState] = useState([])

    // Group like SalesProjects Response Received: quotationId when present, else normalized composite key
    // so QA (populated AssignedGroup / ObjectIds) matches Operations Tracker "Response Received".
    useEffect(() => {
        if (!projects?.length) {
            setResponseReceivedState([]);
            return;
        }

        const projectsGroupedByName = projects.reduce((acc, project) => {
            const compositeKey = projectResponseReceivedCompositeKey(project);
            const existingGroup = acc.find((g) => g._compositeKey === compositeKey);
            if (existingGroup) {
                existingGroup.Projects.push(project);
            } else {
                acc.push({
                    _compositeKey: compositeKey,
                    ProjectName: project.ProjectName,
                    ClientName: project.ClientName,
                    AssignedGroup: project.AssignedGroup,
                    Projects: [project],
                });
            }
            return acc;
        }, []);

        const qualifiedGroups = projectsGroupedByName.filter((group) => {
            return group.Projects.every((project) => {
                if (!project?.Assignedperson?.length) return false;

                const managerEmail = project.AssignedManagerEmail?.[0];
                const manager = employees.find((emp) => emp.EmpEmail === managerEmail);
                const managerId = manager?._id;

                const hasManagerEntries =
                    managerId &&
                    (entries.some(
                        (entry) =>
                            entry.projectid === project._id &&
                            entry.taskownerid === managerId
                    ) ||
                        items.some(
                            (item) =>
                                item.projectid === project._id &&
                                item.taskownerid === managerId
                        ) ||
                        tasks.some(
                            (task) =>
                                task.projectid === project._id &&
                                task.taskownerid === managerId
                        ));

                const matchingLead = leads.find(
                    (lead) =>
                        leadResponseReceivedCompositeKey(lead) ===
                        projectResponseReceivedCompositeKey(project)
                );
                const leadStatus = matchingLead?.Status?.toLowerCase()?.trim();
                const isWonOrLost =
                    leadStatus === "won" ||
                    leadStatus === "won recurring" ||
                    leadStatus === "lost";

                const hasCorrectStatus = project.Assignedperson.every(
                    (person) =>
                        isSalespersonResponseReceived(person) &&
                        isProjectOutstandingCleared(person)
                );

                return hasCorrectStatus || (hasManagerEntries && !isWonOrLost);
            });
        });

        const groupedProjects = qualifiedGroups.map((group) => {
            const matchingLead = leads.find(
                (lead) =>
                    leadResponseReceivedCompositeKey(lead) ===
                    groupRowCompositeKeyForLeadMatch(group)
            );
            const p0 = group.Projects[0];
            return {
                isRead: matchingLead?.isRead || false,
                AssignedGroup: p0.AssignedGroup,
                groupid:
                    matchingLead?.groupid?._id ||
                    matchingLead?.groupid ||
                    p0?.groupid?._id ||
                    p0?.groupid ||
                    null,
                ProjectName: group.ProjectName,
                ClientName:
                    group.ClientName || group.Projects[0]?.ClientName || "",
                DateRequested: p0.DateRequested,
                ProjectDueDate: p0.ProjectDueDate,
                Projects: group.Projects,
                quotationId:
                    matchingLead?.quotationId ||
                    p0?.quotationId ||
                    null,
                leadId:
                    matchingLead?.id ??
                    matchingLead?._id ??
                    null,
            };
        });

        setResponseReceivedState(groupedProjects);
    }, [projects, leads, entries, items, employees, tasks]);

    useEffect(() => {
        if (selectedLeadQuotationId) {
            const sel = String(selectedLeadQuotationId).trim();
            const matched = responseReceivedState?.filter((proj) => {
                const qid =
                    proj?.quotationId != null
                        ? String(proj.quotationId).trim()
                        : "";
                const lid =
                    proj?.leadId != null ? String(proj.leadId).trim() : "";
                return (qid && qid === sel) || (lid && lid === sel);
            });
            setProjectsReceived(matched);
        } else if (operationProjectName) {
            const matched = responseReceivedState?.filter(
                (proj) => proj?.ProjectName === operationProjectName
            );
            setProjectsReceived(matched);
        }
    }, [selectedLeadQuotationId, operationProjectName, responseReceivedState]);

    // Helper function to get unique row identifier
    const getRowUniqueKey = (row) => {
        return row?.quotationId || row?.leadId || `${row?.ProjectName}||${row?.ClientName}||${row?.AssignedGroup}`;
    };

    const [activeTaskKey, setActiveTaskKey] = useState(null);
    const [probabilityList] = useState([]);
    const [isLoading, SetisLoading] = useState(false);
    const [rowid, setrowid] = useState(null);
    const [openInputReceived, setOpenInputReceived] = useState(null);
    const [selected, setSelected] = useState(false);
    const [showExpensesForProject, setShowExpensesForProject] = useState(null); // Track project index that should show expenses
    const [perdiem, setperdiem] = useState(0);
    const [averagecost, setaveragecost] = useState(0);
    const [searchedLeadReports, setsearchedLeadReports] = useState([]);
    const [expenseTableData, setexpenseTableData] = useState("");
    const [highlightedSection, setHighlightedSection] = useState(null); // Track which section (hours or expenses) is highlighted


    const [hourlyRateData, setHourlyRateData] = useState([]);
    useEffect(() => {
        const email = localStorage.getItem("email")
        const employee = employees.find((emp) => emp.EmpEmail === email);

        const filtereddatat = data.filter(
            (proj) => proj.createdBy === employee?._id
        );

        setHourlyRateData(filtereddatat)
    }, [data, employees]);

    useEffect(() => {
        setsearchedLeadReports(projectsReceived)
    }, [projectsReceived]);

    useEffect(() => {
        if (!showExpensesForProject) {
            setHighlightedSection(null);
        }
    }, [showExpensesForProject]);

    useEffect(() => {
        const expensesForUser = scopeOtherExpensesForUser(otherExpenses, {
            loggedUserId: loggedUserIdForExpenses,
        });
        const { perdiem: p, averagecost: a } =
            getPerdiemAndAverageCostFromExpenses(expensesForUser);
        setperdiem(p);
        setaveragecost(a);
    }, [otherExpenses, loggedUserIdForExpenses]);

    const responsereceiveddref = useRef(null);
    useEffect(() => {
        if ((rowid)) {
            // Scroll to the right end of the container
            const scrollToPosition = responsereceiveddref.current.scrollWidth;
            responsereceiveddref.current.scrollTo({
                left: scrollToPosition,
                behavior: 'smooth'
            });
        }
    }, [rowid]);

    const dropdownRefOpenInput = useRef([]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                openInputReceived !== null &&
                dropdownRefOpenInput.current[openInputReceived] &&
                !dropdownRefOpenInput.current[openInputReceived].contains(event.target)
            ) {
                setOpenInputReceived(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openInputReceived]);

    const handleCancel = async (project) => {
        const projectId = project._id;
        SetisLoading(true);
        try {
            await dispatch(cancelManagerFromProjects(projectId)).unwrap();
            enqueueSnackbar("Manager Removed Successfully", {
                variant: "success",
            });
            // Reset status to project requested and go back to Project Request Outstanding
            dispatch(setOperationProjectStatus("project requested"));
            dispatch(fetchAllProjects());
            dispatch(fetchLeads());
            // Stay on step 3 but show Project Request Outstanding
        } catch (error) {
            console.error("Failed to create task:", error);
        } finally {
            SetisLoading(false);
            dispatch(fetchLeads());
        }
        dispatch(fetchAllProjects());
    };

    const handleReject = async (row, project, index, Status) => {
        const projectId = project._id
        SetisLoading(true);
        try {
            await dispatch(salespersonRejectProject({ projectId }));
            enqueueSnackbar("Project Rejected Successfully", { variant: "success" });
            // Reset status to project requested and go back to Project Request Outstanding
            dispatch(setOperationProjectStatus("project requested"));
            dispatch(fetchAllProjects());
        } catch (error) {
            console.error("Update error:", error);
            enqueueSnackbar("Update failed. See console for details.", {
                variant: "error",
            });
        } finally {
            SetisLoading(false);
            setOpenInputReceived(openInputReceived === index ? null : index);
            dispatch(fetchLeads());
            dispatch(setCreatedProject(createdProject));
            // Stay on step 3 but show Project Request Outstanding
        }
    };

    const calculateHardwareTotal = (projectID, typeService, items) => {
        if (!items) return 0;

        const hardwareCost = items.filter(
            (desc) => desc.projectid === projectID && desc.typeservice === typeService
        );

        let hardwareValue = 0;
        const hardwareByDescription = {};

        // Group hardware by assemblepartdescription and keep only the one with highest totalprice
        hardwareCost.forEach((hw) => {
            const description = hw.assemblepartdescription;

            if (!hardwareByDescription[description]) {
                hardwareByDescription[description] = hw;
            } else {
                if (hw.totalprice > hardwareByDescription[description].totalprice) {
                    hardwareByDescription[description] = hw;
                }
            }
        });

        // Calculate total hardware value
        Object.values(hardwareByDescription).forEach((hw) => {
            if (hw.totalprice) {
                hardwareValue += hw.totalprice;
            }
        });

        return hardwareValue;
    };

    const getHardwareValues = useCallback(
        (projectID, typeService) => {
            if (!items) return [];
            return items.filter(
                (desc) =>
                    desc.projectid === projectID &&
                    // desc.taskownerid === personID &&
                    desc.typeservice === typeService
            );
        },
        [items]
    );

    const buildFinalMappedEntries = (matchedEntries, employees) => {
        if (!matchedEntries.length || !employees.length) return [];

        const mapped = matchedEntries.map((entry) => {
            const matchedEmp = employees.find((emp) => emp._id === entry.taskownerid);

            // Check if this is a hardware entry
            const isHardwareEntry =
                entry.hasOwnProperty("assemblepartdescription") ||
                entry.hasOwnProperty("partno") ||
                entry.hasOwnProperty("quantity");

            if (isHardwareEntry) {
                return {
                    createdBy: {
                        email: matchedEmp?.EmpEmail || "",
                        name: `${matchedEmp?.EmpFirstName || ""} ${matchedEmp?.EmpLastName || ""
                            }`,
                        role: matchedEmp?.customRoles || [],
                    },
                    assemblepartdescription: entry.assemblepartdescription,
                    partno: entry.partno,
                    quantity: entry.quantity,
                    unitprice: entry.unitprice,
                    totalprice: entry.totalprice,
                    permonth: entry.permonth,
                    taskstatus: entry.taskstatus,
                    _id: entry._id,
                    taskownerid: entry.taskownerid,
                    TypeService: entry.typeservice || "Hardware",
                    projectid: entry.projectid,
                };
            } else {
                return {
                    createdBy: {
                        email: matchedEmp?.EmpEmail || "",
                        name: `${matchedEmp?.EmpFirstName || ""} ${matchedEmp?.EmpLastName || ""
                            }`,
                        role: matchedEmp?.customRoles || [],
                    },
                    task: entry.task,
                    hoursassigned: entry.hoursassigned,
                    taskStatus: entry.taskStatus,
                    notes: entry.notes,
                    _id: entry._id,
                    taskownerid: entry.taskownerid,
                    TypeService: entry.typeservice || matchedEmp?.servicetype,
                    projectid: entry.projectid,
                };
            }
        });

        return mapped;
    };

    const calculateDescriptionTotal = (
        projectID,
        personID,
        typeService,
        tasks,
        perdiem,
        averagecost
    ) => {
        if (!tasks) return 0;

        const descriptions = tasks.filter(
            (desc) => desc.projectid === projectID && desc.taskownerid === personID
        );

        let totalValue = descriptions.reduce(
            (sum, desc) => sum + (desc.value || 0),
            0
        );

        // Check if this is an Installation service
        if (typeService && typeService.toLowerCase().includes("installation")) {
            let maxTripDuration = 0;
            const uniqueTripDurations = new Set();

            descriptions.forEach((desc) => {
                if (desc.tripduration) {
                    uniqueTripDurations.add(desc.tripduration);
                    maxTripDuration = Math.max(maxTripDuration, desc.tripduration);
                }
            });

            if (maxTripDuration > 0) {
                totalValue += maxTripDuration * perdiem + maxTripDuration * averagecost;
            }
        }

        return totalValue;
    };

    const getTotalHardwareCostValue = useCallback(
        (projectID, personID, typeService) => {
            let hardwareValue = 0;
            const hardwareCost = getHardwareValues(projectID, typeService);
            const hardwareByDescription = {};

            // Group hardware by assemblepartdescription and keep only the one with highest totalprice
            hardwareCost.forEach((hw) => {
                const description = hw.assemblepartdescription;

                if (!hardwareByDescription[description]) {
                    hardwareByDescription[description] = hw;
                } else {
                    // If we already have this description, keep the one with higher totalprice
                    if (hw.totalprice > hardwareByDescription[description].totalprice) {
                        hardwareByDescription[description] = hw;
                    }
                }
            });

            // Calculate total hardware value using only the highest totalprice items
            Object.values(hardwareByDescription).forEach((hw) => {
                if (hw.totalprice) {
                    hardwareValue += hw.totalprice;
                }
            });
            return hardwareValue
            // return hardwareCost.reduce((sum, desc) => sum + (desc.totalprice || 0), 0);
        },
        [getHardwareValues]
    );

    const getTransformedQuotationData = (
        filteredQuotation,
        employees,
        tasks,
        items,
        perdiem,
        averagecost,
        projects // Add projects parameter to check reassignment status
    ) => {
        if (!filteredQuotation || !employees) return [];

        const result = [];

        Object.entries(filteredQuotation).forEach(([projectName, typeServices]) => {
            Object.entries(typeServices).forEach(([typeService, itemsArray]) => {
                itemsArray.forEach((item) => {
                    if (!item?.WorkEntries?.length) return;

                    // Always prioritize manager data over operations person data
                    const relatedProject = projects?.find(
                        (proj) => proj.ProjectName === projectName && proj.TypeService === typeService
                    );
                    
                    // Get manager ID from AssignedManagerEmail
                    let managerIdToFilter = null;
                    if (relatedProject?.AssignedManagerEmail?.[0]) {
                        const manager = employees.find(
                            (emp) => emp.EmpEmail === relatedProject.AssignedManagerEmail[0]
                        );
                        managerIdToFilter = manager?._id;
                    }

                    // Always prioritize manager's entries: if manager has entries, use only manager's entries
                    // This ensures manager's updated data replaces operations person's original data
                    let workEntriesToProcess = item.WorkEntries;
                    if (managerIdToFilter) {
                        const managerEntries = item.WorkEntries.filter(
                            (entry) => entry.taskownerid === managerIdToFilter
                        );
                        // If manager has entries, use only manager's entries (replace operations person data)
                        if (managerEntries.length > 0) {
                            workEntriesToProcess = managerEntries;
                        }
                    }

                    // Group work entries by person
                    const groupedByPerson = {};
                    const personRoleMap = {};

                    workEntriesToProcess.forEach((entry) => {
                        const personName = entry?.createdBy?.name;
                        const personRoles = entry?.createdBy?.role || [];
                        const personId = entry?.taskownerid;

                        if (!groupedByPerson[personName]) {
                            groupedByPerson[personName] = { totalHours: 0, personId };
                        }
                        groupedByPerson[personName].totalHours += entry.hoursassigned || 0;

                        if (!personRoleMap[personName]) {
                            personRoleMap[personName] = { roles: personRoles, personId };
                        }
                    });

                    // Convert grouped persons into array with costs
                    Object.entries(groupedByPerson).forEach(([name, personData]) => {
                        const totalHours = personData.totalHours;
                        const personId = personData.personId;
                        const ProjectID = item?.ProjectID;
                        const ProjectNotes = item?.ProjectNotes;
                        const ServiceQuotedValue = item?.QuotedValue;
                        const servicewiseMargin = item?.ProjectMargin;

                        // Match quotation by personid
                        const matchedQuotation = item?.quotationDetails?.find(
                            (q) => q.personid === personId
                        );

                        const currentEmployee = employees.find((emp) => {
                            const fullName = `${emp.EmpFirstName} ${emp.EmpLastName}`
                                .trim()
                                .toLowerCase();
                            return fullName === name.trim().toLowerCase();
                        });

                        const capitalizedRoles =
                            currentEmployee?.customRoles?.map(
                                (role) =>
                                    role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
                            ) || [];

                        const hourlyRate =
                            (parseFloat(matchedQuotation?.hourlysalaray) || 0) +
                            (parseFloat(matchedQuotation?.overheadpercentage) || 0);

                        const totalCost = totalHours * hourlyRate;

                        // Calculate description total
                        const descriptionTotal = calculateDescriptionTotal(
                            item.ProjectID,
                            personId,
                            item.TypeService,
                            tasks,
                            perdiem,
                            averagecost
                        );

                        // Calculate hardware total
                        const hardwareTotal = calculateHardwareTotal(
                            item.ProjectID,
                            item.TypeService,
                            items
                        );

                        const hasProjectManagerRole =
                            capitalizedRoles.includes("Project manager");

                        result.push({
                            servicewiseMargin,
                            ServiceQuotedValue,
                            projectName,
                            typeService: item.TypeService,
                            resourceType: item.ResourceType,
                            personId,
                            ProjectID,
                            ProjectNotes,
                            personName: name,
                            roles: capitalizedRoles,
                            totalHours,
                            hourlyRate,
                            totalCost,
                            descriptionTotal,
                            hardwareTotal,
                            grandTotal: item?.TypeService?.toLowerCase().includes("hardware")
                                ? (hasProjectManagerRole ? hardwareTotal : 0) + descriptionTotal
                                : descriptionTotal + totalCost,
                        });
                    });
                });
            });
        });

        return result;
    };

    const getFilteredQuotationForProject = (
        row,
        projects,
        employees,
        entries,
        items,
        tasks,
        data,
        perdiem,
        averagecost
    ) => {
        if (
            !row ||
            !projects ||
            !employees ||
            !entries ||
            !items ||
            !tasks ||
            !data
        ) {
            console.log("Missing required data");
            return null;
        }

        const projectName = row.ProjectName || row.projectName;
        const typeService = row.TypeService; // ADD THIS LINE - get the specific service type
        if (!projectName || !typeService) return null; // ADD typeService check

        // Step 1: Find the specific project with BOTH name and service type
        const specificProject = projects.find(
            (proj) =>
                proj.ProjectName === projectName &&
                proj.TypeService === typeService // ADD this filter
        );

        if (!specificProject) {
            console.log(`Project not found: ${projectName} - ${typeService}`);
            return null;
        }

        // Step 2: Always prioritize manager data over operations person data
        // Get manager ID from AssignedManagerEmail
        const managerEmail = specificProject.AssignedManagerEmail?.[0];
        const manager = employees.find((emp) => emp.EmpEmail === managerEmail);
        const managerId = manager?._id;

        // Step 3: Get matched entries for this specific project
        let projectEntries = entries.filter(
            (entry) => entry.projectid === specificProject._id
        );
        let projectItems = items.filter(
            (item) => item.projectid === specificProject._id
        );

        // Always prioritize manager's entries: if manager has entries, use only manager's entries
        // This ensures manager's updated data replaces operations person's original data
        if (managerId) {
            const managerEntries = projectEntries.filter(
                (entry) => entry.taskownerid === managerId
            );
            const managerItems = projectItems.filter(
                (item) => item.taskownerid === managerId
            );
            
            // If manager has entries, use only manager's entries (replace operations person data)
            if (managerEntries.length > 0 || managerItems.length > 0) {
                projectEntries = managerEntries;
                projectItems = managerItems;
            }
        }

        // Combine entries and items for matchedEntries
        const matchedEntries = [...projectEntries, ...projectItems];

        // Step 4: Build finalMappedEntries
        const finalMappedEntries = buildFinalMappedEntries(
            matchedEntries,
            employees
        );

        // Step 5: Build groupedProj for this specific project
        const groupedProj = {};
        groupedProj[projectName] = {};

        const projectId = specificProject._id;

        // Filter entries by the specific service type
        const matchedEntriesForProject = finalMappedEntries.filter(
            (entry) =>
                entry.TypeService === typeService && entry.projectid === projectId
        );

        if (matchedEntriesForProject.length > 0) {
            const salesperson = employees.find(
                (emp) => emp.EmpEmail === specificProject.SalesPersonEmail
            );

            const filteredProject = {
                TypeService: specificProject.TypeService,
                ResourceType: specificProject.ResourceType,
                ProjectName: specificProject.ProjectName,
                QuotedValue: specificProject.ServiceValue,
                ProjectMargin: specificProject.Salesmargin,
                WorkEntries: matchedEntriesForProject,
                AssignedGroup: specificProject.AssignedGroup,
                AssignedManagerEmail: specificProject.AssignedManagerEmail,
                Assignedperson: specificProject.Assignedperson,
                ProjectID: specificProject._id,
                ProjectNotes: specificProject?.ProjectFinanceNotes,
                SalesPersonId: salesperson ? salesperson._id : null,
            };

            if (!groupedProj[projectName][typeService]) {
                groupedProj[projectName][typeService] = [];
            }

            groupedProj[projectName][typeService].push(filteredProject);
        }

        // Step 5: Match quotations
        const result = {};
        result[projectName] = {};

        Object.entries(groupedProj[projectName]).forEach(
            ([serviceType, serviceArray]) => {
                const updatedServiceArray = serviceArray.map((entry) => {
                    const matchedQuotations = data.filter(
                        (q) =>
                            q.designation
                                ?.toLowerCase()
                                .includes(serviceType.toLowerCase()) &&
                            q.createdBy === entry.SalesPersonId
                    );

                    return {
                        ...entry,
                        quotationDetails: matchedQuotations,
                    };
                });

                result[projectName][serviceType] = updatedServiceArray;
            }
        );

        return result;
    };

    const getDescriptionValues = useCallback(
        (projectID, personID, typeService) => {
            if (!tasks) return [];
            return tasks.filter(
                (desc) => desc.projectid === projectID && desc.taskownerid === personID
            );
        },
        [tasks]
    );

    const getTotalDescriptionValue = useCallback(
        (projectID, personID, typeService) => {
            const descriptions = getDescriptionValues(projectID, personID, typeService);

            let totalValue = descriptions.reduce((sum, desc) => sum + (desc.value || 0), 0);

            // Check if this is an Installation service
            if (typeService && typeService.toLowerCase().includes('installation')) {
                // Find the maximum trip duration for this person (to avoid duplicate counting)
                let maxTripDuration = 0;
                const uniqueTripDurations = new Set();

                descriptions.forEach(desc => {
                    if (desc.tripduration) {
                        uniqueTripDurations.add(desc.tripduration);
                        maxTripDuration = Math.max(maxTripDuration, desc.tripduration);
                    }
                });
                if (maxTripDuration > 0) {
                    // Add: (tripduration * perdiem) + (tripduration * averageCost)
                    totalValue += (maxTripDuration * perdiem) + (maxTripDuration * averagecost);
                }
            }

            return totalValue;
        },
        [getDescriptionValues, perdiem, averagecost] // Add perdiem and averageCost to dependencies
    );

    const transformFilteredQuotation = useCallback((filteredQuotation, employees, projects) => {
        const result = [];

        Object.entries(filteredQuotation || {}).forEach(([projectName, typeServices]) => {
            Object.entries(typeServices).forEach(([typeService, items]) => {
        items.forEach((item) => {
            if (!item?.WorkEntries?.length) return;

            // Always prioritize manager data over operations person data
            const relatedProject = projects?.find(
                (proj) => proj.ProjectName === projectName && proj.TypeService === typeService
            );
            
            // Get manager ID from AssignedManagerEmail
            let managerIdToFilter = null;
            if (relatedProject?.AssignedManagerEmail?.[0]) {
                const manager = employees.find(
                    (emp) => emp.EmpEmail === relatedProject.AssignedManagerEmail[0]
                );
                managerIdToFilter = manager?._id;
            }

            // Always prioritize manager's entries: if manager has entries, use only manager's entries
            // This ensures manager's updated data replaces operations person's original data
            let workEntriesToProcess = item.WorkEntries;
            if (managerIdToFilter) {
                const managerEntries = item.WorkEntries.filter(
                    (entry) => entry.taskownerid === managerIdToFilter
                );
                // If manager has entries, use only manager's entries (replace operations person data)
                if (managerEntries.length > 0) {
                    workEntriesToProcess = managerEntries;
                }
            }

                    // Group work entries by person
                    const groupedByPerson = {};
                    const personRoleMap = {};

                    workEntriesToProcess.forEach((entry) => {
                        const personName = entry?.createdBy?.name;
                        const personRoles = entry?.createdBy?.role || [];
                        const personId = entry?.taskownerid;

                        if (!groupedByPerson[personName]) {
                            groupedByPerson[personName] = { totalHours: 0, personId };
                        }
                        groupedByPerson[personName].totalHours += entry.hoursassigned || 0;

                        if (!personRoleMap[personName]) {
                            personRoleMap[personName] = { roles: personRoles, personId };
                        }
                    });
                    // Convert grouped persons into array with costs
                    Object.entries(groupedByPerson).forEach(([name, personData]) => {
                        const totalHours = personData.totalHours;
                        const personId = personData.personId;
                        const ProjectID = item?.ProjectID;
                        const ProjectNotes = item?.ProjectNotes;
                        const ServiceQuotedValue = item?.QuotedValue;
                        const servicewiseMargin = item?.ProjectMargin;

                        // Get roles for this person
                        const personRoleInfo = personRoleMap[name];
                        const personRoles = personRoleInfo?.roles || [];
                        // Find matching quotation based on roles
                        let matchedQuotation = null;

                        if (item.quotationDetails) {
                            // Try to match by checking if any role matches the designation
                            // Since personRoles is an array and designation is a string, we need to check each role
                            for (const role of personRoles) {
                                matchedQuotation = item.quotationDetails.find(q =>
                                    q.designation.toLowerCase() === role.toLowerCase()
                                );

                                if (matchedQuotation) {
                                    console.log(`Matched role "${role}" with designation "${matchedQuotation.designation}"`);
                                    break;
                                }
                            }

                            // If no match found with exact role, try partial matching
                            if (!matchedQuotation) {
                                for (const role of personRoles) {
                                    matchedQuotation = item.quotationDetails.find(q =>
                                        q.designation.toLowerCase().includes(role.toLowerCase()) ||
                                        role.toLowerCase().includes(q.designation.toLowerCase())
                                    );

                                    if (matchedQuotation) {
                                        console.log(`Partially matched role "${role}" with designation "${matchedQuotation.designation}"`);
                                        break;
                                    }
                                }
                            }
                        }

                        console.log("Matched Quotation:", matchedQuotation);

                        const currentEmployee = employees.find((emp) => {
                            const fullName = `${emp.EmpFirstName} ${emp.EmpLastName}`.trim().toLowerCase();
                            return fullName === name.trim().toLowerCase();
                        });

                        const capitalizedRoles =
                            currentEmployee?.customRoles?.map(
                                (role) =>
                                    role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
                            ) || [];

                        // Use matched quotation hourly rate, fallback to 0 if not found
                        const hourlyRate = matchedQuotation
                            ? (parseFloat(matchedQuotation?.hourlysalaray) || 0) +
                              (parseFloat(matchedQuotation?.overheadpercentage) || 0)
                            : 0;

                        const totalCost = totalHours * hourlyRate;

                        // External helpers
                        const descriptionTotal = getTotalDescriptionValue(
                            item.ProjectID,
                            personId,
                            item.TypeService
                        );

                        const hardwareTotal = getTotalHardwareCostValue(
                            item.ProjectID,
                            personId,
                            item.TypeService
                        );

                        const hasProjectManagerRole = capitalizedRoles.includes("Project manager");
                        result.push({
                            servicewiseMargin,
                            ServiceQuotedValue,
                            projectName,
                            typeService: item.TypeService,
                            resourceType: item.ResourceType,
                            personId,
                            ProjectID,
                            ProjectNotes,
                            personName: name,
                            roles: capitalizedRoles,
                            totalHours,
                            hourlyRate,
                            totalCost,
                            descriptionTotal,
                            hardwareTotal,
                            grandTotal:
                                item?.TypeService?.toLowerCase().includes("hardware")
                                    ? (hasProjectManagerRole ? hardwareTotal : 0) + descriptionTotal
                                    : descriptionTotal + totalCost,
                            matchedDesignation: matchedQuotation?.designation || 'Not matched'
                        });
                    });
                });
            });
        });

        return result;
    }, [getTotalDescriptionValue, getTotalHardwareCostValue]);


    const groupByTypeService = (processedData) => {
        return processedData.reduce((acc, curr) => {
            if (!acc[curr.typeService]) {
                acc[curr.typeService] = [];
            }
            acc[curr.typeService].push(curr);
            return acc;
        }, {});
    };



    const handleAccept = async (row, project, index, Status) => {
        // REFACTORED: Find projects using composite key (ProjectName + ClientName + AssignedGroup)
        // UNDO: Change back to filtering by ProjectName only if reverting
        const allRelatedProjects = projects.filter((proj) => {
            const projKey = `${proj.ProjectName || ''}||${proj.ClientName || ''}||${proj.AssignedGroup || ''}`.toLowerCase().trim();
            const projectKey = `${project.ProjectName || ''}||${project.ClientName || ''}||${project.AssignedGroup || ''}`.toLowerCase().trim();
            return projKey === projectKey;
        });

        // Process each related project to get aggregated data
        const allGroupedData = {};

        allRelatedProjects.forEach((relatedProject) => {
            const filteredQuotationData = getFilteredQuotationForProject(
                relatedProject,
                projects,
                employees,
                entries,
                items,
                tasks,
                hourlyRateData,
                perdiem,
                averagecost
            );

            if (filteredQuotationData) {
                // Get transformed data for this specific project
                let transformedData = [];
                try {
                    transformedData = getTransformedQuotationData(
                        filteredQuotationData,
                        employees,
                        tasks,
                        items,
                        perdiem,
                        averagecost,
                        projects
                    );
                } catch (error) {
                    console.error("Error transforming data for", relatedProject.TypeService, ":", error);
                }

                console.log(`Transformed data for ${relatedProject.TypeService}:`, transformedData);

                // Process and merge the data
                const processedData = transformFilteredQuotation(filteredQuotationData, employees, projects);
                const groupedData = groupByTypeService(processedData);

                // Merge this groupedData into the main allGroupedData object
                Object.entries(groupedData).forEach(([typeService, dataArray]) => {
                    if (!allGroupedData[typeService]) {
                        allGroupedData[typeService] = [];
                    }
                    allGroupedData[typeService].push(...dataArray);
                });

                console.log(`Grouped data for ${relatedProject.TypeService}:`, groupedData);
            }
        });

        console.log("Combined groupedData for all services:", allGroupedData);

        // Now you have allGroupedData containing data for ALL service types
        // Example structure:
        // {
        //   "Software": [{...}, {...}],
        //   "Cloud": [{...}, {...}],
        //   "Hardware": [{...}]
        // }

        // You can calculate totals across all services
        let totalCostAllServices = 0;
        let serviceBreakdown = {};

        Object.entries(allGroupedData).forEach(([typeService, dataArray]) => {
            const serviceTotal = dataArray.reduce((sum, item) => sum + (item.grandTotal || 0), 0);
            serviceBreakdown[typeService] = {
                total: serviceTotal,
                items: dataArray.length,
                people: dataArray.map(item => item.personName)
            };
            totalCostAllServices += serviceTotal;
        });

        console.log("Service Breakdown:", serviceBreakdown);
        console.log("Total Cost for all services:", totalCostAllServices);


        const clickedServiceType = project.TypeService;
        const clickedServiceBreakdown = {};

        if (serviceBreakdown[clickedServiceType]) {
            clickedServiceBreakdown[clickedServiceType] = serviceBreakdown[clickedServiceType];
        }

        console.log("Filtered service breakdown (only clicked service):", clickedServiceBreakdown);

        const updatedAssignedPerson = (project.Assignedperson || []).map(
            (person) => ({
                ...person,
                salespersonaction: "accept",
            })
        );

        const updatedRow = {
            ProjectName: project.ProjectName,
            ProjectDueDate: project.ProjectDueDate || "",
            updateData: {
                AssignedManagerEmail: project.AssignedManagerEmail,
                Assignedperson: updatedAssignedPerson,
                WorkEntries: project.WorkEntries,
                // SalesPersonAction: "accept",
            },
        };
        const formData = new FormData();
        formData.append("project", JSON.stringify(updatedRow));
        SetisLoading(true);
        const typeservicecost =
            serviceBreakdown[clickedServiceType]?.total || 0;
        const projectId = project._id;
        const totaltypeservicecost = totalCostAllServices;
        console.log("maindata after", project._id, typeservicecost, totalCostAllServices);

        try {
            await dispatch(
                salespersonAcceptAction({
                    projectId,
                    typeservicecost,
                    totaltypeservicecost,
                })
            ).unwrap();
            enqueueSnackbar("Project Accepted Successfully", { variant: "success" });
            dispatch(fetchAllProjects());
            // After accepting, check if all responses are now accepted
            // The useEffect will handle the redirect to Generate Quotation
        } catch (error) {
            console.error("Update error:", error);
            enqueueSnackbar("Update failed. See console for details.", {
                variant: "error",
            });
        } finally {
            SetisLoading(false);
            setOpenInputReceived(openInputReceived === index ? null : index);
        }
    };

    const handleAcceptAll = async (row, index) => {
        const projectsToAccept = (row?.Projects || []).filter(
            (project) =>
                !(project?.Assignedperson || []).every(
                    (person) => person?.salespersonaction === "accept"
                )
        );

        if (!projectsToAccept.length) return;

        const referenceProject = projectsToAccept[0];
        // Same composite-key related-project scope as single Accept in wizard
        const allRelatedProjects = projects.filter((proj) => {
            const projKey = `${proj.ProjectName || ""}||${proj.ClientName || ""}||${proj.AssignedGroup || ""}`
                .toLowerCase()
                .trim();
            const projectKey = `${referenceProject.ProjectName || ""}||${referenceProject.ClientName || ""}||${referenceProject.AssignedGroup || ""}`
                .toLowerCase()
                .trim();
            return projKey === projectKey;
        });

        const allGroupedData = {};

        allRelatedProjects.forEach((relatedProject) => {
            const filteredQuotationData = getFilteredQuotationForProject(
                relatedProject,
                projects,
                employees,
                entries,
                items,
                tasks,
                hourlyRateData,
                perdiem,
                averagecost
            );

            if (filteredQuotationData) {
                try {
                    getTransformedQuotationData(
                        filteredQuotationData,
                        employees,
                        tasks,
                        items,
                        perdiem,
                        averagecost,
                        projects
                    );
                } catch (error) {
                    console.error(
                        "Error transforming data for",
                        relatedProject.TypeService,
                        ":",
                        error
                    );
                }

                const processedData = transformFilteredQuotation(
                    filteredQuotationData,
                    employees,
                    projects
                );
                const groupedData = groupByTypeService(processedData);

                Object.entries(groupedData).forEach(([typeService, dataArray]) => {
                    if (!allGroupedData[typeService]) {
                        allGroupedData[typeService] = [];
                    }
                    allGroupedData[typeService].push(...dataArray);
                });
            }
        });

        let totalCostAllServices = 0;
        const serviceBreakdown = {};

        Object.entries(allGroupedData).forEach(([typeService, dataArray]) => {
            const serviceTotal = dataArray.reduce(
                (sum, item) => sum + (item.grandTotal || 0),
                0
            );
            serviceBreakdown[typeService] = {
                total: serviceTotal,
                items: dataArray.length,
                people: dataArray.map((item) => item.personName),
            };
            totalCostAllServices += serviceTotal;
        });

        SetisLoading(true);
        try {
            for (const project of projectsToAccept) {
                const typeservicecost =
                    serviceBreakdown[project.TypeService]?.total || 0;
                await dispatch(
                    salespersonAcceptAction({
                        projectId: project._id,
                        typeservicecost,
                        totaltypeservicecost: totalCostAllServices,
                    })
                ).unwrap();
            }
            enqueueSnackbar("All Projects Accepted Successfully", {
                variant: "success",
            });
            dispatch(fetchAllProjects());
        } catch (error) {
            console.error("Update error:", error);
            enqueueSnackbar("Update failed. See console for details.", {
                variant: "error",
            });
        } finally {
            SetisLoading(false);
            setOpenInputReceived(openInputReceived === index ? null : index);
        }
    };

    const [operationclicked, setOperationclicked] = useState(false)
    const [hardwareEntries, setHardwareEntries] = useState([]);
    const [Entries, setSelectedManagerEntries] = useState([]);
    const [managerDescriptions, setManagerDescriptions] = useState([]);

    const getManagerEntries = (project) => {

        const isHardware = project?.TypeService?.toLowerCase().includes("hardware");
        const foundEmployee = employees.find(emp => emp.EmpEmail === project.AssignedManagerEmail[0]);

        // Always prioritize manager data over operations person data
        // Match entries by project ID and manager ID
        let matchedEntries =
            entries?.filter((entry) => entry.projectid === project._id
                && entry.taskownerid === foundEmployee?._id
            ) || [];

        // For hardware projects, get hardware-related entries from `items`
        let matchedEntriesHardware = isHardware
            ? items?.filter((entry) => entry.projectid === project._id
                && entry.taskownerid === foundEmployee?._id
            ) || []
            : [];

        let matchedHardwareDescriptions =
            tasks?.filter((entry) => entry.projectid === project._id
                && entry.taskownerid === foundEmployee?._id
            ) || [];

        // Always prioritize manager's entries: if manager has entries, use only manager's entries
        // This ensures manager's updated data replaces operations person's original data
        if (foundEmployee?._id) {
            // Check if manager has any entries
            const managerEntries = entries?.filter((entry) => entry.projectid === project._id
                && entry.taskownerid === foundEmployee._id
            ) || [];
            
            const managerHardwareEntries = isHardware
                ? items?.filter((entry) => entry.projectid === project._id
                    && entry.taskownerid === foundEmployee._id
                ) || []
                : [];
            
            const managerDescriptions = tasks?.filter((entry) => entry.projectid === project._id
                && entry.taskownerid === foundEmployee._id
            ) || [];

            // If manager has entries, use only manager's entries (replace operations person data)
            if (managerEntries.length > 0) {
                matchedEntries = managerEntries;
            }
            if (managerHardwareEntries.length > 0) {
                matchedEntriesHardware = managerHardwareEntries;
            }
            if (managerDescriptions.length > 0) {
                matchedHardwareDescriptions = managerDescriptions;
            }
        }

        const currentEmployee = employees.find((emp) =>
            project.AssignedManagerEmail.includes(emp.EmpEmail)
        );

        if (!currentEmployee) {
            return {
                managerEntries: [],
                hardwareEntries: matchedEntriesHardware,
            };
        }

        const managerEntries = matchedEntries.filter(
            (entry) => entry.taskownerid === currentEmployee._id
        );
        const managerDescription = matchedHardwareDescriptions.filter(
            (entry) => entry.taskownerid === currentEmployee._id
        );

        return {
            managerEntries,
            hardwareEntries: matchedEntriesHardware,
            managerDescription,
        };
    };

    const getEntriesManager = (project) => {
        const { managerEntries } = getManagerEntries(project); // ✅ correctly extract array

        if (project?.TypeService?.toLowerCase().includes("hardware")) {
            // For hardware projects, show consolidated count (all parts from all assigned persons)
            const consolidatedHardware = getConsolidatedHardwareEntries(project);
            return consolidatedHardware.length === 0 ? " " : `${consolidatedHardware.length} Parts`;
        }
        const totalHours = managerEntries.reduce(
            (sum, entry) => sum + (entry.hoursassigned || 0),
            0
        );
        return totalHours === 0 ? " " : `${totalHours} Hrs`;

    };

    const getOtherExpenseManager = (project) => {
        const { managerDescription } = getManagerEntries(project);
        const sumOfManagerDescription = sumOtherExpensesLineItemsOnly(
            managerDescription
        );

        return sumOfManagerDescription === 0 ? " " : ` ${formatAmountComma(sumOfManagerDescription)}`;
    };

    const getOperationEntries = (project, OpPerson) => {
        let currentOPEmployee = null;

        if (
            OpPerson.salespersonstatus === "response received" &&
            OpPerson.taskStatus === "submit"
        ) {
            currentOPEmployee = employees.find(
                (emp) => emp.EmpEmail === OpPerson.email
            );
        }

        const isHardware = project?.TypeService?.toLowerCase().includes("hardware")

        const matchedEntries =
            entries?.filter(
                (entry) =>
                    entry.projectid === project?._id &&
                    entry.taskownerid === currentOPEmployee?._id
            ) || [];

        const operationDescription =
            tasks?.filter(
                (entry) =>
                    entry.projectid === project?._id &&
                    entry.taskownerid === currentOPEmployee?._id
            ) || [];

        const matchedHardwareEntries = isHardware
            ? items?.filter(
                (entry) =>
                    entry.projectid === project?._id &&
                    entry.taskownerid === currentOPEmployee?._id
            ) || []
            : [];

        return {
            operationEntries: matchedEntries,
            hardwareEntries: matchedHardwareEntries,
            operationDescription,
        };
    };

    const getEntriesOperation = (project, person) => {
        const { operationEntries } = getOperationEntries(
            project,
            person
        );

        if (project?.TypeService?.toLowerCase().includes("hardware")) {
            // For hardware projects, show consolidated count (all parts from all assigned persons)
            const consolidatedHardware = getConsolidatedHardwareEntries(project);
            return consolidatedHardware.length;
        }

        const totalHours = operationEntries.reduce(
            (sum, entry) => sum + (entry.hoursassigned || 0),
            0
        );

        return totalHours;
    };

    const getOtherexpensesOperation = (project, person) => {
        const OtherexpenesOperation = getOperationEntries(project, person);
        const sumOfManagerDescription = sumOtherExpensesLineItemsOnly(
            OtherexpenesOperation.operationDescription
        );
        return sumOfManagerDescription === 0 ? " " : ` ${formatAmountComma(sumOfManagerDescription)}`;
    };

    const [sortedProjects, setSortedProjects] = useState([]);

    useEffect(() => {
        setSortedProjects(projectsReceived);
    }, [projectsReceived]);

    const [showActionToAssign, setShowActionToAssign] = useState({
        0: "tasktable",
    });

    const getAllHoursTotal = (project) => {

        const assignedEmails = project.Assignedperson?.map(person =>
            person.email?.trim().toLowerCase()
        ) || [];

        const matchingEmployees = employees?.filter(employee => {
            const employeeEmail = employee.EmpEmail?.trim().toLowerCase();
            return assignedEmails.includes(employeeEmail);
        });

        const matchingEmployeeIds = matchingEmployees?.map(employee => employee._id);

        const filteredWorkEntries = entries?.filter(entry => {
            return matchingEmployeeIds?.includes(entry.taskownerid) &&
                entry.projectid === project._id;
        });
        const totalHours = filteredWorkEntries.reduce((sum, entry) => {
            return sum + (entry.hoursassigned || 0);
        }, 0);

        const filteredHardwareEntries = items?.filter(entry => {
            return matchingEmployeeIds?.includes(entry.taskownerid) &&
                entry.projectid === project._id;
        });

        const uniqueHardwareEntries = filteredHardwareEntries?.filter((entry, index, array) => {
            const firstIndex = array.findIndex(item =>
                item.assemblepartdescription === entry.assemblepartdescription
            );
            return index === firstIndex;
        });

        if (project.TypeService?.toLowerCase().includes("hardware")) {
            return uniqueHardwareEntries.length;
        } else {
            return totalHours;
        }
    };

    // Get consolidated hardware entries for a project (all unique parts from all assigned persons)
    const getConsolidatedHardwareEntries = (project) => {
        if (!project?.TypeService?.toLowerCase().includes("hardware")) {
            return [];
        }

        const assignedEmails = project.Assignedperson?.map(person =>
            person.email?.trim().toLowerCase()
        ) || [];

        const matchingEmployees = employees?.filter(employee => {
            const employeeEmail = employee.EmpEmail?.trim().toLowerCase();
            return assignedEmails.includes(employeeEmail);
        });

        const matchingEmployeeIds = matchingEmployees?.map(employee => employee._id);

        const filteredHardwareEntries = items?.filter(entry => {
            return matchingEmployeeIds?.includes(entry.taskownerid) &&
                entry.projectid === project._id;
        });

        // Group by assemblepartdescription and keep the one with highest totalprice
        const hardwareByDescription = {};
        filteredHardwareEntries?.forEach((entry) => {
            const description = entry.assemblepartdescription;
            if (!hardwareByDescription[description]) {
                hardwareByDescription[description] = entry;
            } else {
                // Keep the one with higher totalprice
                if (parseFloat(entry.totalprice || 0) > parseFloat(hardwareByDescription[description].totalprice || 0)) {
                    hardwareByDescription[description] = entry;
                }
            }
        });

        // Return array of unique hardware entries
        return Object.values(hardwareByDescription);
    };

    const [financeDataProject, setfinanceDataProject] = useState([]);
    useEffect(() => {
        const filteredProject = projects.filter(
            (proj) => proj.isProjecttracking === false
        );

        const qualifiedGroups = filteredProject.filter((project) => {
            // Must have at least one assigned person
            if (!project?.Assignedperson?.length) return false;

            // At least ONE assigned person must meet criteria
            return project.Assignedperson.some((person) =>

            (
                person.salespersonstatus === "response received"
            )
            );
        });
        setfinanceDataProject(qualifiedGroups)
    }, [projects]);

    const [searchedCalcProjects, setSearchedCalcProjects] = useState([]);

    // REFACTORED: Use composite key matching instead of just ProjectName
    // UNDO: Change back to simple ProjectName matching if reverting
    useEffect(() => {
        if (!searchedLeadReports || !financeDataProject) return; // guard
        const matchedProjects = financeDataProject.filter((row) =>
            searchedLeadReports.some((proj) => {
                const projKey = `${proj.ProjectName || ''}||${proj.ClientName || ''}||${proj.AssignedGroup || ''}`.toLowerCase().trim();
                const rowKey = `${row.ProjectName || ''}||${row.ClientName || ''}||${row.AssignedGroup || ''}`.toLowerCase().trim();
                return projKey === rowKey;
            })
        );
        setSearchedCalcProjects(matchedProjects);
    }, [searchedLeadReports, financeDataProject]);
    const [mergedProjects, setMergedProjects] = useState([]);

    useEffect(() => {
        if (!searchedCalcProjects || !tasks || !entries) return;

        const newData = searchedCalcProjects.map((proj) => {
            const matchedEntries = entries.filter(
                (entry) => entry.projectid === proj._id
            );

            const matchedDescriptions = tasks.filter(
                (task) => task.projectid === proj._id
            );
            const matchedHardware = items.filter(
                (task) => task.projectid === proj._id
            );

            return {
                ProjectName: proj.ProjectName,
                ClientName: proj.ClientName || '', // REFACTORED: Include ClientName for composite matching
                AssignedGroup: proj.AssignedGroup || '', // REFACTORED: Include AssignedGroup for composite matching
                ProjectID: proj._id,
                TaskList: matchedEntries,
                Description: matchedDescriptions,
                TypeService: proj.TypeService,
                HardwareList: matchedHardware,
                Assignedperson: proj.Assignedperson,
                AssignedManagerEmail: proj.AssignedManagerEmail[0],
            };
        });

        setMergedProjects(newData);
    }, [searchedCalcProjects, tasks, entries, items]);

    const [combinedProjects, setCombinedProjects] = useState([]);

    useEffect(() => {
        if (!mergedProjects || mergedProjects.length === 0) return;

        const grouped = Object.values(
            mergedProjects.reduce((acc, project) => {
                if (!acc[project.ProjectName]) {
                    acc[project.ProjectName] = {
                        ProjectName: project.ProjectName,
                        projects: [], // Array to hold all projects with this name
                    };
                }

                // Push the entire project object as-is to the projects array
                acc[project.ProjectName].projects.push({
                    ProjectID: project.ProjectID,
                    TypeService: project.TypeService,
                    AssignedManagerEmail: project.AssignedManagerEmail,
                    Assignedperson: project.Assignedperson || [],
                    Description: project.Description || [],
                    HardwareList: project.HardwareList || [],
                    TaskList: project.TaskList || [],
                    // Add any other fields you need
                });

                return acc;
            }, {})
        );

        setCombinedProjects(grouped);
    }, [mergedProjects]);

    const [groupedProjects, setGroupedProjects] = useState([]);


    useEffect(() => {
        if (!combinedProjects || !employees) return;

        const updatedProjects = combinedProjects.map((group) => {
            // Process each project in the group
            const updatedGroupProjects = group.projects.map((project) => {
                // helper to map arrays
                const enrichWithDesignation = (arr) =>
                    arr.map((item) => {
                        // Prefer taskownerid (person who did the work) for rate/designation; createdBy can be manager and would assign wrong rate
                        const emp = employees.find((e) => e._id === item.taskownerid)
                            || employees.find((e) => e._id === item.createdBy);
                        if (emp) {
                            return {
                                ...item,
                                designation: `${emp.servicetype || ""} ${emp.customRoles?.[0] || ""}`.trim(),
                            };
                        }
                        return item;
                    });

                return {
                    ...project,
                    Description: project.Description
                        ? enrichWithDesignation(project.Description)
                        : [],
                    TaskList: project.TaskList
                        ? enrichWithDesignation(project.TaskList)
                        : [],
                    HardwareList: project.HardwareList
                        ? enrichWithDesignation(project.HardwareList)
                        : [],
                };
            });

            return {
                ...group,
                projects: updatedGroupProjects,
            };
        });

        setGroupedProjects(updatedProjects);
    }, [combinedProjects, employees]);
    const [quotationProjects, setquotationProjects] = useState([]);

    useEffect(() => {
        if (!groupedProjects || !hourlyRateData) return;

        const updatedProjects = groupedProjects.map((projectGroup) => {
            // Process each project within the group
            const updatedProjectsInGroup = projectGroup.projects.map((project) => {
                const typeServiceNorm = (project?.TypeService || "").toLowerCase().trim().replace(/\s+/g, " ");
                const enrichWithQuotation = (arr) =>
                    arr.map((item) => {
                        if (!item.designation) return item;
                        // Rates from fetchSalesHourlyRates only (hourlyRateData). Match by designation.
                        const normalize = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");
                        const itemDes = normalize(item.designation);
                        const isOperations = itemDes.includes("operations") && !itemDes.includes("manager");
                        const isManager = itemDes.includes("manager") || itemDes.includes("project manager");
                        let matchedQuote = null;
                        // Operations role -> prefer rate whose designation includes this project's service type (e.g. "installation")
                        if (isOperations) {
                            if (typeServiceNorm) {
                                matchedQuote = hourlyRateData.find((q) => {
                                    const qDes = normalize(q.designation);
                                    return qDes && qDes.includes(typeServiceNorm) && qDes.includes("operations") && !qDes.includes("manager");
                                });
                            }
                            if (!matchedQuote) {
                                matchedQuote = hourlyRateData.find((q) => {
                                    const qDes = normalize(q.designation);
                                    return qDes && qDes.includes("operations") && !qDes.includes("manager");
                                });
                            }
                        }
                        // Manager role -> prefer rate whose designation includes this project's service type (e.g. "installation project manager")
                        if (!matchedQuote && isManager) {
                            if (typeServiceNorm) {
                                matchedQuote = hourlyRateData.find((q) => {
                                    const qDes = normalize(q.designation);
                                    return qDes && qDes.includes(typeServiceNorm) && (qDes.includes("project manager") || qDes.includes("manager"));
                                });
                            }
                            if (!matchedQuote) {
                                matchedQuote = hourlyRateData.find((q) => {
                                    const qDes = normalize(q.designation);
                                    return qDes && (qDes.includes("project manager") || qDes.includes("manager"));
                                });
                            }
                        }
                        // Fallback: exact or ends-with match (prefer service-type match)
                        if (!matchedQuote && itemDes) {
                            if (typeServiceNorm) {
                                matchedQuote = hourlyRateData.find((q) => {
                                    const qDes = normalize(q.designation);
                                    return qDes && qDes.includes(typeServiceNorm) && (qDes === itemDes || qDes.endsWith(" " + itemDes));
                                });
                            }
                            if (!matchedQuote) {
                                matchedQuote = hourlyRateData.find((q) => {
                                    const qDes = normalize(q.designation);
                                    return qDes && (qDes === itemDes || qDes.endsWith(" " + itemDes));
                                });
                            }
                        }
                        if (matchedQuote) {
                            return {
                                ...item,
                                hourlysalary:
                                    (parseFloat(matchedQuote.hourlysalaray) || 0) +
                                    (parseFloat(matchedQuote.overheadpercentage) || 0),
                            };
                        }
                        return item;
                    });

                return {
                    ...project,
                    TaskList: project.TaskList ? enrichWithQuotation(project.TaskList) : [],
                    Description: project.Description
                        ? enrichWithQuotation(project.Description)
                        : [],
                    HardwareList: project.HardwareList
                        ? enrichWithQuotation(project.HardwareList)
                        : [],
                };
            });

            return {
                ...projectGroup,
                projects: updatedProjectsInGroup,
            };
        });

        setquotationProjects(updatedProjects);
    }, [groupedProjects, hourlyRateData]);

    const [, setCalculatedProjects] = useState([]);

    useEffect(() => {
        if (!quotationProjects || quotationProjects.length === 0) return;

        const newData = quotationProjects.map((proj) => {
            let taskValue = 0;
            let descriptionValue = 0;
            let hardwareValue = 0;

            // ✅ TaskList calculation
            if (proj.TaskList?.length > 0) {
                const hoursByOwner = {};

                proj.TaskList.forEach((task) => {
                    if (task.taskownerid && task.hoursassigned) {
                        if (!hoursByOwner[task.taskownerid]) {
                            const rate =
                                task.hourlysalary != null
                                    ? task.hourlysalary
                                    : (parseFloat(task.hourlysalaray) || 0) +
                                      (parseFloat(task.overheadpercentage) || 0);
                            hoursByOwner[task.taskownerid] = {
                                totalHours: 0,
                                hourlyRate: rate,
                            };
                        }
                        hoursByOwner[task.taskownerid].totalHours += task.hoursassigned;
                    }
                });

                Object.values(hoursByOwner).forEach((ownerData) => {
                    taskValue += ownerData.totalHours * ownerData.hourlyRate;
                });
            }
            if (proj.Description?.length > 0) {
                // Create a map to track trip duration per taskownerid for Installation services
                const installationTripByOwner = {};
                proj.Description.forEach((desc) => {
                    // For Installation services, track trip duration per owner
                    if (desc.typeservice && desc.typeservice.toLowerCase().includes('installation') &&
                        desc.taskownerid && desc.tripduration) {
                        if (!installationTripByOwner[desc.taskownerid]) {
                            installationTripByOwner[desc.taskownerid] = desc.tripduration;
                        } else {
                            // Use the maximum trip duration for each owner
                            installationTripByOwner[desc.taskownerid] =
                                Math.max(installationTripByOwner[desc.taskownerid], desc.tripduration);
                        }
                    }
                    // Add the normal value calculation
                    if (desc.value) {
                        descriptionValue += desc.value;
                    }
                });
                // Add Installation trip duration calculations (per diem + other expenses)
                Object.values(installationTripByOwner).forEach((tripDuration) => {
                    // Assuming you have state variables called perdiem and otherExpense
                    descriptionValue += (tripDuration * perdiem) + (tripDuration * averagecost);
                });
            }
            // ✅ HardwareList calculation
            if (proj.HardwareList?.length > 0) {
                proj.HardwareList.forEach((hw) => {
                    if (hw.totalprice) {
                        hardwareValue += hw.totalprice;
                    }
                });
            }
            return {
                ProjectName: proj.ProjectName,
                ClientName: proj.ClientName || proj.projects?.[0]?.ClientName || '', // REFACTORED: Include ClientName for composite matching
                AssignedGroup: proj.AssignedGroup || proj.projects?.[0]?.AssignedGroup || '', // REFACTORED: Include AssignedGroup for composite matching
                TaskListValue: taskValue,
                DescriptionValue: descriptionValue,
                HardwareListValue: hardwareValue,
            };
        });

        setCalculatedProjects(newData);
    }, [quotationProjects, perdiem, averagecost]);


    const getAllExpenses = (project) => {
        const matchedProjectfinal = quotationProjects
            .find((proj) => proj.ProjectName === project.ProjectName)
            ?.projects.find((p) => p.ProjectID === project._id);

        if (!matchedProjectfinal) return 0;

        const totalTaskValue = (matchedProjectfinal?.TaskList || []).reduce(
            (total, task) => {
                const hoursSpent = Number(task.hoursassigned) || 0;
                let effectiveHourlyRate = 0;
                if (task.hourlysalary !== undefined && task.hourlysalary !== null) {
                    effectiveHourlyRate = parseFloat(task.hourlysalary) || 0;
                } else {
                    const hourlySalary = parseFloat(task.hourlysalaray) || 0;
                    const overhead = parseFloat(task.overheadpercentage) || 0;
                    effectiveHourlyRate = hourlySalary + overhead;
                }
                return total + hoursSpent * effectiveHourlyRate;
            },
            0
        );

        let descriptionValue = 0;

        if (matchedProjectfinal?.Description?.length > 0) {
            const installationTripByOwner = {};

            matchedProjectfinal.Description.forEach((desc) => {
                if (
                    desc.typeservice &&
                    desc.typeservice.toLowerCase().includes("installation") &&
                    desc.taskownerid &&
                    desc.tripduration
                ) {
                    if (!installationTripByOwner[desc.taskownerid]) {
                        installationTripByOwner[desc.taskownerid] = desc.tripduration;
                    } else {
                        installationTripByOwner[desc.taskownerid] = Math.max(
                            installationTripByOwner[desc.taskownerid],
                            desc.tripduration
                        );
                    }
                }

                if (desc.value) {
                    descriptionValue += Number(desc.value) || 0;
                }
            });

            Object.values(installationTripByOwner).forEach((tripDuration) => {
                descriptionValue +=
                    tripDuration * (Number(perdiem) || 0) +
                    tripDuration * (Number(averagecost) || 0);
            });
        }

        let hardwareValue = 0;
        if (matchedProjectfinal?.TypeService?.toLowerCase().includes("hardware")) {
            if (matchedProjectfinal?.HardwareList?.length > 0) {
                const hardwareByDescription = {};
                matchedProjectfinal.HardwareList.forEach((hw) => {
                    const key = hw.assemblepartdescription;
                    if (!key) return;
                    const price = parseFloat(hw.totalprice) || 0;
                    if (
                        !hardwareByDescription[key] ||
                        price > (parseFloat(hardwareByDescription[key].totalprice) || 0)
                    ) {
                        hardwareByDescription[key] = hw;
                    }
                });
                Object.values(hardwareByDescription).forEach((hw) => {
                    if (hw.totalprice) {
                        hardwareValue += Number(hw.totalprice) || 0;
                    }
                });
            }
        }

        return totalTaskValue + descriptionValue + hardwareValue;
    };

    const getExpensestableData = (project) => {
        const matchedProjectfinal = quotationProjects.find(
            (proj) => proj.ProjectName === project.ProjectName
        )?.projects.find(
            (p) => p.ProjectID === project._id
        );
        setexpenseTableData(matchedProjectfinal)
    };

    const { matchedProject, SalesStatus } = useMatchedProject(operationProjectName, selectedLeadQuotationId);

    const calculatedProjectData = useMatchedOperationsData(
        matchedProject[0]?.Projects || []
    );

    const [isAllManagerAccepted, setIsAllManagerAccepted] = useState(false);
    const operationProjectStatus = useSelector((state) => state.wizardstep.operationProjectStatus);

    useEffect(() => {
        // Don't check if we're already on generate quotation or other statuses
        if (operationProjectStatus === "generate quotation") {
            return;
        }

        if (!calculatedProjectData || !createdProject || createdProject.length === 0) return;
        
        // REFACTORED: Prioritize quotationId matching for unique lead identification
        // This ensures that when multiple leads have same ProjectName/ClientName/Group,
        // only the selected lead's projects are checked for acceptance
        const matchedProject = calculatedProjectData?.find((data) => {
            // First try to match by quotationId if available (most specific)
            if (selectedLeadQuotationId && data?.quotationId != null) {
                return String(data.quotationId) === String(selectedLeadQuotationId);
            }
            // Fallback to composite key matching if quotationId is not available
            const dataKey = `${data?.ProjectName || ''}||${createdProject[0]?.ClientName || ''}||${data?.AssignedGroup || createdProject[0]?.AssignedGroup || ''}`.toLowerCase().trim();
            const projectKey = `${createdProject[0]?.ProjectName || ''}||${createdProject[0]?.ClientName || ''}||${createdProject[0]?.AssignedGroup || ''}`.toLowerCase().trim();
            return dataKey === projectKey;
        });

        if (!matchedProject || !matchedProject.Projects) {
            setIsAllManagerAccepted(false);
            return;
        }

        const matchedManagers = matchedProject.Projects; // array

        // Check if all projects have all assigned persons accepted
        // A project is considered "accepted" only if:
        // 1. It has assigned persons
        // 2. ALL assigned persons have salespersonaction === "accept"
        const allAccepted = matchedManagers?.every((project) => {
            // If project has no assigned persons, it might be cancelled - consider as not accepted
            if (!project?.Assignedperson || project.Assignedperson.length === 0) return false;
            
            // Check if ALL assigned persons are accepted
            return project.Assignedperson.every(
                (person) => person?.salespersonaction === "accept"
            );
        });

        // Check if any response is rejected or cancelled
        const hasRejectedOrCancelled = matchedManagers?.some((project) => {
            if (!project?.Assignedperson || project.Assignedperson.length === 0) {
                // Project with no assigned persons might be cancelled
                return true;
            }
            return project.Assignedperson.some(
                (person) => person?.salespersonaction === "reject"
            );
        });

        // If any response is rejected/cancelled, reset to project requested
        if (hasRejectedOrCancelled) {
            setIsAllManagerAccepted(false);
            if (operationProjectStatus !== "project requested") {
                dispatch(setOperationProjectStatus("project requested"));
            }
            return;
        }

        // CRITICAL: Only redirect if ALL managers/projects are accepted
        // Check that we have at least one project and ALL projects have all their assigned persons accepted
        // Also ensure that there are no projects with pending (null/undefined) salespersonaction
        const hasPendingAcceptance = matchedManagers?.some((project) => {
            if (!project?.Assignedperson || project.Assignedperson.length === 0) return true;
            // Check if any assigned person doesn't have "accept" action (could be null, undefined, or other value)
            return project.Assignedperson.some(
                (person) => person?.salespersonaction !== "accept"
            );
        });

        // In ERP module: Do NOT auto-redirect - user must click Next button
        // Only update the isAllManagerAccepted state to enable/disable Next button
        if (subscriptionPackage === "ERP") {
            // Update state to enable/disable Next button, but do NOT auto-redirect
            if (allAccepted && !hasPendingAcceptance && matchedManagers.length > 0 && operationProjectStatus === "response received") {
                setIsAllManagerAccepted(true);
            } else {
                // If not all accepted, disable Next button
                setIsAllManagerAccepted(false);
            }
        } else {
            // For CRM module, use original auto-redirect logic
            if (allAccepted && matchedManagers.length > 0 && operationProjectStatus === "response received") {
                setIsAllManagerAccepted(true);
                dispatch(setOperationProjectStatus("generate quotation"));
                dispatch(setCreatedProject(createdProject));
                dispatch(goNext());
            } else if (!allAccepted) {
                setIsAllManagerAccepted(false);
            }
        }
    }, [calculatedProjectData, createdProject, projects, dispatch, operationProjectStatus, subscriptionPackage, selectedLeadQuotationId]);

    const handleNextRR = () => {
        // In ERP module: Navigate to P&L table (project approved status) when all managers are accepted
        if (subscriptionPackage === "ERP" && isAllManagerAccepted) {
            // Set status to "project approved" to show P&L table at step 4
            dispatch(setOperationProjectStatus("project approved"));
            dispatch(setCreatedProject(createdProject));
            // Navigate to step 4 (P&L table) - use goToStep to ensure we go to step 4
            dispatch(goToStep(4));
        } else {
            // For CRM module or other cases, use SalesStatus
            dispatch(setOperationProjectStatus(SalesStatus))
            dispatch(setCreatedProject(createdProject));
            dispatch(goNext());
        }
    }

    return (
        <>
            {isLoading ? <AdminLoader isLoading={isLoading} /> : <> </>}
            <div className="pt-3">
                <div ref={responsereceiveddref} className="w-[99%] overflow-auto bg-white h-full  min-h-[20vh] max-h-[90vh] ">
                    <div className="">
                        <div className="flex text-xs w-full text-center text-black">
                            <div className={` ${rowid ? "" : "w-[80%]"} font-semibold`}>
                                <div className={`flex `}>
                                    <div className="w-12 bg-[#E8E8E8] h-12 flex justify-around items-center align-middle">Sr. No</div>
                                    <div className={` ${rowid ? "w-28" : "w-[20%]"} h-12  bg-[#E8E8E8] p-2 break-words text-wrap flex justify-start items-center align-middle`}>
                                        <span className="pl-4">Group</span>
                                    </div>
                                    <div className={`${rowid ? "w-28" : "w-[20%]"} h-12 border bg-[#E8E8E8] p-2 break-words text-wrap flex justify-start items-center align-middle`}>
                                        Client Name
                                    </div>
                                    <div className={`${rowid ? "w-28" : "w-[20%]"} h-12 bg-[#E8E8E8] p-2 break-words text-wrap flex justify-start items-center align-middle`}>
                                        <span className="pl-3">Project Name</span>
                                    </div>
                                    <div className={`${rowid ? "w-28" : "w-[20%]"} h-12 border bg-[#E8E8E8] p-2 break-words text-wrap flex justify-around items-center align-middle`}>
                                        Date {rowid && <br />}  Requested
                                    </div>
                                    <div className={`${rowid ? "w-28" : "w-[20%]"} h-12 border bg-[#E8E8E8] p-2 break-words text-wrap flex justify-around items-center align-middle`}>
                                        Due Date
                                    </div>
                                </div>
                            </div>
                            {rowid ? (
                                <div className="w-full">
                                    <div className="flex font-semibold">
                                        <div className="w-28 flex-shrink-0 h-12  bg-[#E8E8E8] p-2 break-words text-wrap flex justify-around items-center align-middle">
                                            Manager Details
                                        </div>
                                        <div className="w-24 flex-shrink-0 h-12 bg-[#E8E8E8] p-2 break-words text-wrap flex justify-around items-center align-middle">
                                            Hours/Parts
                                        </div>
                                        <div className="w-32 flex-shrink-0 h-12 bg-[#E8E8E8] p-2 break-words text-wrap flex justify-around items-center align-middle">
                                            Other Expenses ({currency})
                                        </div>
                                        <div className="w-28 flex-shrink-0 h-12 bg-[#E8E8E8] p-2 break-words text-wrap flex justify-around items-center align-middle">
                                            Employees
                                        </div>
                                        <div className="w-24 flex-shrink-0 h-12 bg-[#E8E8E8] p-2 break-words text-wrap flex justify-around items-center align-middle">
                                            Hours/Parts
                                        </div>
                                        <div className="w-32 flex-shrink-0 h-12 bg-[#E8E8E8] p-2 break-words text-wrap flex justify-around items-center align-middle">
                                            Other Expenses ({currency})
                                        </div>
                                        <div className="w-48 flex-shrink-0 h-12 bg-[#E8E8E8]  border break-words text-wrap">
                                            <div className="h-[50%] flex flex-col justify-around items-center align-middle">Total</div>
                                            <div className="flex h-[50%]">
                                                <div className="w-[50%] flex  justify-around items-center align-middle p-2">Hours/Parts</div>
                                                <div className="w-[50%] flex  justify-around items-center align-middle p-2">Expenses ({currency})</div>
                                            </div>
                                        </div>
                                        <div className="w-32 flex-shrink-0 h-12 bg-[#E8E8E8]  border p-2 break-words text-wrap flex justify-around items-center align-middle">
                                            Action
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className={`font-semibold w-[20%] h-12 bg-[#E8E8E8] p-2 break-words text-wrap flex justify-center items-center align-middle`}>
                                    Input Details
                                </div>
                            )}

                        </div>
                        <div className="w-full ">
                            {sortedProjects.map((row, index) => {
                                const matchedLead = leads?.find((proj) => proj?.ProjectName === row?.ProjectName)
                                console.log("updatedRequestedProjects matchedLead", matchedLead)
                                const isAccepted =
                                    row?.Projects?.every((project) => {
                                        return project?.Assignedperson?.every(
                                            (person) => person?.salespersonaction === "accept"
                                        );
                                    }) || false;
                                return (
                                    <div
                                        className={`flex text-xs w-full cursor-pointer ${matchedLead?.isRead === false ? "font-bold" : " "}`}
                                        key={index}
                                    >
                                        <div className={`flex ${rowid ? "" : "w-[80%]"}`}>
                                            <div
                                                onClick={() => {
                                                    setrowid(prev => prev === getRowUniqueKey(row) ? null : getRowUniqueKey(row))
                                                    setSelected(!selected)
                                                }}
                                                className="w-12 p-2 border-b flex justify-start align-middle items-center">{index + 1}
                                            </div>
                                            <div
                                                onClick={() => {
                                                    setrowid(prev => prev === getRowUniqueKey(row) ? null : getRowUniqueKey(row))
                                                    setSelected(!selected)
                                                }}
                                                className={`${rowid ? "w-28 flex-shrink-0" : "w-[20%]"} border-b flex justify-start align-middle items-center  p-2 break-words text-wrap`}>
                                                <span className="pl-4"> {getDisplayGroupName(row)}</span>
                                            </div>
                                            <div
                                                onClick={() => {
                                                    setrowid(prev => prev === getRowUniqueKey(row) ? null : getRowUniqueKey(row))
                                                    setSelected(!selected)
                                                }}
                                                className={`${rowid ? "w-28 flex-shrink-0" : "w-[20%]"} text-black border-b flex justify-start align-middle items-center text-start p-2 break-words text-wrap overflow-hidden text-ellipsis line-clamp-2 break-all leading-snug text-xs`}>
                                                {createdProject?.[0]?.ClientName}
                                            </div>
                                            <div
                                                onClick={() => {
                                                    setrowid(prev => prev === getRowUniqueKey(row) ? null : getRowUniqueKey(row))
                                                    setSelected(!selected)
                                                }}
                                                className={`${rowid ? "w-28 flex-shrink-0" : "w-[20%]"} border-b flex justify-start align-middle items-center text-center p-2 break-words text-wrap overflow-hidden text-ellipsis line-clamp-2 break-all leading-snug text-xs`}>
                                                <span className="pl-3">{row.ProjectName}</span>
                                            </div>
                                            <div onClick={() => {
                                                setrowid(prev => prev === getRowUniqueKey(row) ? null : getRowUniqueKey(row))
                                                setSelected(!selected)
                                            }}
                                                className={`${rowid ? "w-28 flex-shrink-0" : "w-[20%]"} border-b flex justify-center align-middle items-center  p-2 break-words text-wrap`}>
                                                {row.DateRequested && !isNaN(new Date(row.DateRequested))
                                                    ? formatDateByCountry(row.DateRequested)
                                                    : ""}
                                            </div>
                                            <div
                                                onClick={() => {
                                                    setrowid(prev => prev === getRowUniqueKey(row) ? null : getRowUniqueKey(row))
                                                    setSelected(!selected)
                                                }}
                                                className={`${rowid ? "w-28 flex-shrink-0" : "w-[20%]"} border-b flex justify-center align-middle items-center  p-2 break-words text-wrap`}>
                                                {row.ProjectDueDate &&
                                                    !isNaN(new Date(row.ProjectDueDate))
                                                    ? formatDateByCountry(row.ProjectDueDate)
                                                    : ""}
                                            </div>
                                        </div>

                                        {rowid === getRowUniqueKey(row) ? (
                                            <div className="projetdetails ">
                                                {row.Projects?.map((project, projectIndex) => (
                                                    <>
                                                        <div
                                                            key={`project-${projectIndex}`}
                                                            className="flex"
                                                        >
                                                            <div className="flex ManagerRole">
                                                                <div className="ManagerDetails border-b w-28 flex-shrink-0 ManagerName text-center break-words flex justify-center items-center">
                                                                    <div
                                                                        onClick={() => {
                                                                            const key = `manager_${index}_${projectIndex}`;

                                                                            const {
                                                                                managerEntries,
                                                                                hardwareEntries,
                                                                                managerDescription,
                                                                            } = getManagerEntries(project);
                                                                            // For hardware projects, use consolidated entries instead of individual entries
                                                                            const consolidatedHardware = project?.TypeService?.toLowerCase().includes("hardware")
                                                                                ? getConsolidatedHardwareEntries(project)
                                                                                : hardwareEntries;
                                                                            setHardwareEntries(consolidatedHardware);
                                                                            setManagerDescriptions(managerDescription);
                                                                            setSelectedManagerEntries(managerEntries); // ✅ store clicked manager's entries
                                                                            setActiveTaskKey((prevKey) =>
                                                                                prevKey === key ? null : key
                                                                            );
                                                                            setShowActionToAssign((prev) => ({
                                                                                ...prev,
                                                                                [index]: "tasktable", // Set to tasktable for hours
                                                                            }));
                                                                            setOperationclicked(false)
                                                                            setShowExpensesForProject(null)
                                                                        }}
                                                                        className={`
                                 ${activeTaskKey === `manager_${index}_${projectIndex}`
                                                                                ? "text-blue-500 rounded-md p-2"
                                                                                : ""
                                                                            }`}
                                                                    >
                                                                        {employees
                                                                            .filter((employee) =>
                                                                                project.AssignedManagerEmail?.includes(
                                                                                    employee.EmpEmail
                                                                                )
                                                                            )
                                                                            .map((emp) => {
                                                                                const fullName =
                                                                                    `${emp.EmpFirstName.charAt(
                                                                                        0
                                                                                    ).toUpperCase()}${emp.EmpFirstName.slice(
                                                                                        1
                                                                                    )} ` +
                                                                                    `${emp.EmpLastName.charAt(
                                                                                        0
                                                                                    ).toUpperCase()}${emp.EmpLastName.slice(
                                                                                        1
                                                                                    )}`;

                                                                                const service = `(${emp.servicetype || ""
                                                                                    }`;

                                                                                const role =
                                                                                    emp.customRoles[0] === "project manager"
                                                                                        ? "Manager)"
                                                                                        : "Employee)";

                                                                                return `${fullName} ${service} ${role}`;
                                                                            })
                                                                            .join(", ")}
                                                                    </div>
                                                                </div>


                                                                <div className="Hours/Parts flex justify-center items-center">
                                                                    <div
                                                                        onClick={() => {
                                                                            if (getEntriesManager(project) !== " ") {
                                                                                const key = `manager_${index}_${projectIndex}`;
                                                                                const {
                                                                                    managerEntries,
                                                                                    hardwareEntries,
                                                                                    managerDescription,
                                                                                } = getManagerEntries(project);
                                                                                // For hardware projects, use consolidated entries instead of individual entries
                                                                                const consolidatedHardware = project?.TypeService?.toLowerCase().includes("hardware")
                                                                                    ? getConsolidatedHardwareEntries(project)
                                                                                    : hardwareEntries;
                                                                                setHardwareEntries(consolidatedHardware);
                                                                                setManagerDescriptions(managerDescription);
                                                                                setSelectedManagerEntries(managerEntries);
                                                                                setActiveTaskKey((prevKey) => prevKey === key ? null : key);
                                                                                setShowActionToAssign((prev) => ({
                                                                                    ...prev,
                                                                                    [index]: "tasktable", // Set to tasktable for hours
                                                                                }));
                                                                                setOperationclicked(false)
                                                                                setShowExpensesForProject(null)
                                                                            }

                                                                        }}
                                                                        className={`p-2 w-24 flex-shrink-0 h-full border-b Manager Hours flex items-center justify-center text-center break-words align-middle cursor-pointer
                                     ${activeTaskKey === `manager_${index}_${projectIndex}` &&
                                                                                showActionToAssign[index] === "tasktable" ?
                                                                                "text-blue-500 rounded-md" : ""}`}
                                                                    >
                                                                        {getEntriesManager(project) === " " ? " " : getEntriesManager(project)}
                                                                    </div>
                                                                </div>
                                                                <div className="OtherExpenses flex justify-center items-center">
                                                                    <div
                                                                        onClick={() => {
                                                                            if (getOtherExpenseManager(project) !== " ") {
                                                                                const key = `manager_${index}_${projectIndex}`;
                                                                                const {
                                                                                    managerEntries,
                                                                                    hardwareEntries,
                                                                                    managerDescription,
                                                                                } = getManagerEntries(project);
                                                                                setHardwareEntries(hardwareEntries);
                                                                                setManagerDescriptions(managerDescription);
                                                                                setSelectedManagerEntries(managerEntries);
                                                                                setActiveTaskKey((prevKey) => prevKey === key ? null : key);
                                                                                setShowActionToAssign((prev) => ({
                                                                                    ...prev,
                                                                                    [index]: "otherexpenses", // Set to otheexpenses for expenses
                                                                                }));
                                                                                setOperationclicked(false)
                                                                                setShowExpensesForProject(null)
                                                                            }
                                                                        }}
                                                                        className={`p-2 w-32 h-full border-b OMothetexpenses break-words text-wrap flex justify-center items-center align-middle cursor-pointer
    ${activeTaskKey === `manager_${index}_${projectIndex}` &&
                                                                                showActionToAssign[index] === "otherexpenses" ?
                                                                                "text-blue-500 rounded-md" : ""}`}
                                                                    >
                                                                        {getOtherExpenseManager(project)}
                                                                    </div>
                                                                </div>

                                                            </div>

                                                            <div className={`cursor-pointer  flex justify-center items-center`} >
                                                                {/* Team Member Role */}
                                                                <div className="Employees w-28 flex-shrink-0 h-full border-b flex flex-col justify-center items-center">
                                                                    {(() => {
                                                                        const managerEmails =
                                                                            project.AssignedManagerEmail?.map((email) =>
                                                                                email?.trim().toLowerCase()
                                                                            ) || [];

                                                                        const validPersons =
                                                                            project.Assignedperson?.filter((person) => {
                                                                                const personEmail = person.email
                                                                                    ?.trim()
                                                                                    .toLowerCase();
                                                                                return (
                                                                                    personEmail &&
                                                                                    !managerEmails.includes(personEmail) &&
                                                                                    (person.salespersonstatus ===
                                                                                        "response received" ||
                                                                                        person.salespersonstatus === "submit")
                                                                                );
                                                                            });

                                                                        return validPersons?.length ? (
                                                                            validPersons.map((person, personIndex) => {
                                                                                const OpPerson = person;
                                                                                const formattedName = person.name
                                                                                    ?.split(" ")
                                                                                    .map(
                                                                                        (word) =>
                                                                                            word.charAt(0).toUpperCase() +
                                                                                            word.slice(1).toLowerCase()
                                                                                    )
                                                                                    .join(" ");

                                                                                const matchedEmployee = employees.find(
                                                                                    (emp) => {
                                                                                        const fullEmpName =
                                                                                            `${emp.EmpFirstName} ${emp.EmpLastName}`.trim();
                                                                                        return (
                                                                                            fullEmpName.toLowerCase() ===
                                                                                            formattedName.toLowerCase()
                                                                                        );
                                                                                    }
                                                                                );

                                                                                return (
                                                                                    <div
                                                                                        key={`person-${personIndex}`}
                                                                                        className={`break-words p-2 h-full w-full flex justify-center items-center text-center
                                          ${activeTaskKey ===
                                                                                                `operation_${index}_${projectIndex}_${personIndex}`
                                                                                                ? "text-blue-500 rounded-md "
                                                                                                : ""
                                                                                            }`}
                                                                                        onClick={() => {
                                                                                            const key = `operation_${index}_${projectIndex}_${personIndex}`;
                                                                                            const {
                                                                                                hardwareEntries,
                                                                                                operationEntries,
                                                                                                operationDescription,
                                                                                            } = getOperationEntries(
                                                                                                project,
                                                                                                OpPerson
                                                                                            );
                                                                                            // For hardware projects, use consolidated entries instead of individual entries
                                                                                            const consolidatedHardware = project?.TypeService?.toLowerCase().includes("hardware")
                                                                                                ? getConsolidatedHardwareEntries(project)
                                                                                                : hardwareEntries;
                                                                                            setSelectedManagerEntries(
                                                                                                operationEntries
                                                                                            );
                                                                                            setManagerDescriptions(
                                                                                                operationDescription
                                                                                            );
                                                                                            setHardwareEntries(consolidatedHardware);
                                                                                            setActiveTaskKey((prevKey) =>
                                                                                                prevKey === key ? null : key
                                                                                            );
                                                                                            setShowActionToAssign((prev) => ({
                                                                                                ...prev,
                                                                                                [index]: "tasktable", // Set to tasktable for hours2
                                                                                            }));
                                                                                            setOperationclicked(true)
                                                                                            setShowExpensesForProject(null)
                                                                                        }}
                                                                                    >
                                                                                        {matchedEmployee
                                                                                            ? `${matchedEmployee?.servicetype} Employee ${personIndex + 1}` : ""}
                                                                                        {/* {matchedEmployee
                                                                                            ? `${matchedEmployee.EmpFirstName} ${matchedEmployee.EmpLastName}`
                                                                                            : formattedName} */}
                                                                                    </div>
                                                                                );
                                                                            })
                                                                        ) : (
                                                                            <div className="break-words text-center p-2 h-[10] ">

                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>

                                                                {/* Team Member Hours */}
                                                                <div className="Parts w-24 flex-shrink-0  h-full border-b flex flex-col justify-center">
                                                                    {(() => {
                                                                        const managerEmails =
                                                                            project.AssignedManagerEmail?.map((email) =>
                                                                                email?.trim().toLowerCase()
                                                                            ) || [];

                                                                        const validPersons =
                                                                            project.Assignedperson?.filter((person) => {
                                                                                const personEmail = person.email
                                                                                    ?.trim()
                                                                                    .toLowerCase();
                                                                                return (
                                                                                    personEmail &&
                                                                                    !managerEmails.includes(personEmail) &&
                                                                                    (person.salespersonstatus ===
                                                                                        "response received" ||
                                                                                        person.salespersonstatus === "submit")
                                                                                );
                                                                            });

                                                                        return validPersons?.length ? (
                                                                            validPersons.map((person, personIndex) => {
                                                                                return (
                                                                                    <div
                                                                                        key={`hours-${personIndex}`}
                                                                                        className={`break-words text-center p-2 flex justify-center items-center h-full w-full cursor-pointer
    ${activeTaskKey === `operation_${index}_${projectIndex}_${personIndex}` &&
                                                                                                showActionToAssign[index] === "tasktable" ?
                                                                                                "text-blue-500 rounded-md" : ""}`}
                                                                                        onClick={() => {
                                                                                            const key = `operation_${index}_${projectIndex}_${personIndex}`;
                                                                                            const {
                                                                                                hardwareEntries,
                                                                                                operationEntries,
                                                                                                operationDescription,
                                                                                            } = getOperationEntries(project, person);
                                                                                            // For hardware projects, use consolidated entries instead of individual entries
                                                                                            const consolidatedHardware = project?.TypeService?.toLowerCase().includes("hardware")
                                                                                                ? getConsolidatedHardwareEntries(project)
                                                                                                : hardwareEntries;
                                                                                            setHardwareEntries(consolidatedHardware);
                                                                                            setSelectedManagerEntries(operationEntries);
                                                                                            setManagerDescriptions(operationDescription);
                                                                                            setActiveTaskKey((prevKey) => prevKey === key ? null : key);
                                                                                            setShowActionToAssign((prev) => ({
                                                                                                ...prev,
                                                                                                [index]: "tasktable", // Set to tasktable for hours
                                                                                            }));
                                                                                            setOperationclicked(true)
                                                                                            setShowExpensesForProject(null)
                                                                                        }}
                                                                                    >
                                                                                        {getEntriesOperation(project, person)}{" "}
                                                                                        {project?.TypeService?.toLowerCase().includes("hardware") ? "Parts" : "Hrs"}
                                                                                    </div>
                                                                                );
                                                                            })
                                                                        ) : (
                                                                            <div className="break-words text-center p-2 h-[10] flex justify-center items-center">

                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>

                                                                <div className="OtherExpenses OPothetexpenses h-full  border-b w-32 flex-shrink-0 flex flex-col justify-center">
                                                                    {(() => {
                                                                        const managerEmails =
                                                                            project.AssignedManagerEmail?.map((email) =>
                                                                                email?.trim().toLowerCase()
                                                                            ) || [];

                                                                        const validPersons =
                                                                            project.Assignedperson?.filter((person) => {
                                                                                const personEmail = person.email
                                                                                    ?.trim()
                                                                                    .toLowerCase();
                                                                                return (
                                                                                    personEmail &&
                                                                                    !managerEmails.includes(personEmail) &&
                                                                                    (person.salespersonstatus ===
                                                                                        "response received" ||
                                                                                        person.salespersonstatus === "submit")
                                                                                );
                                                                            });

                                                                        return validPersons?.length ? (
                                                                            validPersons.map((person, personIndex) => {
                                                                                return (
                                                                                    <div
                                                                                        key={`expenses-${personIndex}`}
                                                                                        className={`break-words text-center flex justify-center items-center h-full w-full cursor-pointer
    ${activeTaskKey === `operation_${index}_${projectIndex}_${personIndex}` &&
                                                                                                showActionToAssign[index] === "otherexpenses" ?
                                                                                                "text-blue-500 rounded-md " : ""}`}
                                                                                        onClick={() => {
                                                                                            const key = `operation_${index}_${projectIndex}_${personIndex}`;
                                                                                            const {
                                                                                                hardwareEntries,
                                                                                                operationEntries,
                                                                                                operationDescription,
                                                                                            } = getOperationEntries(project, person);
                                                                                            setHardwareEntries(hardwareEntries);
                                                                                            setSelectedManagerEntries(operationEntries);
                                                                                            setManagerDescriptions(operationDescription);
                                                                                            setActiveTaskKey((prevKey) => prevKey === key ? null : key);
                                                                                            setShowActionToAssign((prev) => ({
                                                                                                ...prev,
                                                                                                [index]: "otherexpenses", // Set to otheexpenses for expenses
                                                                                            }));
                                                                                            setOperationclicked(true)
                                                                                            setShowExpensesForProject(null)
                                                                                        }}
                                                                                    >
                                                                                        {getOtherexpensesOperation(project, person)}{" "}
                                                                                    </div>
                                                                                );
                                                                            })
                                                                        ) : (
                                                                            <div className="break-words text-center p-1 h-[10] flex justify-center items-center">

                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>

                                                            <div className="w-48 flex-shrink-0 border-b">
                                                                <div className="flex h-full">
                                                                    <div
                                                                        onClick={() => {
                                                                            // Toggle hours/parts for this specific project
                                                                            const projectKey = `project-${index}-${projectIndex}`;
                                                                            const hoursKey = `${projectKey}-hours`;

                                                                            // Toggle highlight
                                                                            if (showExpensesForProject === projectKey && highlightedSection === hoursKey) {
                                                                                setShowExpensesForProject(null);
                                                                                setHighlightedSection(null);
                                                                                setShowActionToAssign((prev) => {
                                                                                    const newState = { ...prev };
                                                                                    delete newState[index];
                                                                                    return newState;
                                                                                });
                                                                            } else {
                                                                                setShowExpensesForProject(projectKey);
                                                                                setHighlightedSection(hoursKey);
                                                                                getExpensestableData(project);
                                                                                // For hardware projects, set consolidated hardware entries and show parts table
                                                                                if (project?.TypeService?.toLowerCase().includes("hardware")) {
                                                                                    const consolidatedHardware = getConsolidatedHardwareEntries(project);
                                                                                    setHardwareEntries(consolidatedHardware);
                                                                                    // Get manager descriptions for other expenses tab
                                                                                    const { managerDescription } = getManagerEntries(project);
                                                                                    setManagerDescriptions(managerDescription);
                                                                                    // Set showActionToAssign to show parts table
                                                                                    setShowActionToAssign((prev) => ({
                                                                                        ...prev,
                                                                                        [index]: "tasktable",
                                                                                    }));
                                                                                }
                                                                                // Clear expenses section if it was open
                                                                                if (highlightedSection === `${projectKey}-expenses`) {
                                                                                    setHighlightedSection(hoursKey);
                                                                                }
                                                                            }

                                                                            setActiveTaskKey(null);
                                                                        }}
                                                                        className={`w-[50%] border-b flex justify-around items-center align-middle p-2 cursor-pointer ${highlightedSection === `project-${index}-${projectIndex}-hours`
                                                                            ? "text-blue-500 "
                                                                            : "bg-white"
                                                                            }`}
                                                                    >
                                                                        {getAllHoursTotal(project)}
                                                                        {project?.TypeService?.toLowerCase().includes("hardware") ? " Parts" : " Hrs"}
                                                                    </div>
                                                                    <div
                                                                        onClick={() => {
                                                                            // Toggle expenses for this specific project
                                                                            const projectKey = `project-${index}-${projectIndex}`;
                                                                            const expensesKey = `${projectKey}-expenses`;

                                                                            // Toggle highlight
                                                                            if (showExpensesForProject === projectKey && highlightedSection === expensesKey) {
                                                                                setShowExpensesForProject(null);
                                                                                setHighlightedSection(null);
                                                                                setShowActionToAssign((prev) => {
                                                                                    const newState = { ...prev };
                                                                                    delete newState[index];
                                                                                    return newState;
                                                                                });
                                                                            } else {
                                                                                setShowExpensesForProject(projectKey);
                                                                                setHighlightedSection(expensesKey);
                                                                                getExpensestableData(project);
                                                                                // Clear parts table if it was open
                                                                                setShowActionToAssign((prev) => {
                                                                                    const newState = { ...prev };
                                                                                    delete newState[index];
                                                                                    return newState;
                                                                                });
                                                                            }

                                                                            setActiveTaskKey(null);
                                                                        }}
                                                                        className={`allothetexpenses w-[50%] border-b flex justify-around items-center align-middle p-2 cursor-pointer ${highlightedSection === `project-${index}-${projectIndex}-expenses`
                                                                            ? "text-blue-500 "
                                                                            : "bg-white"
                                                                            }`}
                                                                    >
                                                                        {formatAmountComma(getAllExpenses(project))}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="w-32 flex-shrink-0 border-b flex justify-center items-center p-2 break-words text-wrap ">
                                                                <div className="w-full ">
                                                                    <div className="bg-white">
                                                                        <div className="relative flex p-0 rounded-xl align-middle items-center">
                                                                            <select
                                                                                disabled={project.SalesPersonAction === "reject"}
                                                                                value={
                                                                                    probabilityList[index] ||
                                                                                    (project.Assignedperson?.some((p) => p.salespersonaction === "accept")
                                                                                        ? "Accepted"
                                                                                        : project.Assignedperson?.some((p) => p.salespersonaction === "reject")
                                                                                            ? "Rejected"
                                                                                            : "")
                                                                                }
                                                                                onChange={(e) => {
                                                                                    const selected = e.target.value;

                                                                                    if (selected === "Accept") {
                                                                                        handleAccept(row, project, index, "Accept");
                                                                                        setActiveTaskKey(null);
                                                                                        setShowExpensesForProject(null)
                                                                                        setShowActionToAssign({});
                                                                                    } else if (selected === "Reject") {
                                                                                        handleReject(row, project, index, "Reject");
                                                                                        setActiveTaskKey(null);
                                                                                        setShowExpensesForProject(null)
                                                                                        setShowActionToAssign({});
                                                                                    } else if (selected === "Cancel") {
                                                                                        handleCancel(project);
                                                                                        setActiveTaskKey(null);
                                                                                        setShowExpensesForProject(null)
                                                                                        setShowActionToAssign({});
                                                                                    } else if (selected === "Accept All") {
                                                                                        handleAcceptAll(row, index);
                                                                                        setActiveTaskKey(null);
                                                                                        setShowExpensesForProject(null);
                                                                                        setShowActionToAssign({});
                                                                                    }
                                                                                }}
                                                                                className="border statusbutton w-full bg-white  border-gray-400 text-nowrap rounded-md p-2 shadow-md text-start overflow-hidden outline-none  cursor-pointer"
                                                                            >
                                                                                {/* Default Option */}
                                                                                <option hidden value="">
                                                                                    {project.Assignedperson?.some((p) => p.salespersonaction === "accept")
                                                                                        ? "Accepted"
                                                                                        : project.Assignedperson?.some((p) => p.salespersonaction === "reject")
                                                                                            ? "Rejected"
                                                                                            : "Select Option"}
                                                                                </option>

                                                                                {/* Accept Option (only if not all accepted) */}
                                                                                {project.Assignedperson?.some(
                                                                                    (p) => p.salespersonaction !== "accept"
                                                                                ) && (
                                                                                        <option value="Accept">
                                                                                            {project.Assignedperson?.find(
                                                                                                (p) => p.name === row.assignpersonName
                                                                                            )?.manageraction === "rejected" &&
                                                                                                project.Assignedperson?.find(
                                                                                                    (p) => p.name === row.assignpersonName
                                                                                                )?.operationaction === "resubmit"
                                                                                                ? "Accept"
                                                                                                : "Accept"}
                                                                                        </option>
                                                                                    )}

                                                                                {/* Reject Option (only if not all rejected) */}
                                                                                {project.Assignedperson?.some(
                                                                                    (p) => p.salespersonaction !== "reject"
                                                                                ) && <option value="Reject">Reject</option>}

                                                                                {/* Cancel Option (always available) */}
                                                                                <option value="Cancel">Cancel</option>

                                                                                {/* Accept All (pending lead only — hides after all managers accepted) */}
                                                                                {!isAccepted && (
                                                                                    <option value="Accept All">
                                                                                        Accept All
                                                                                    </option>
                                                                                )}
                                                                            </select>
                                                                        </div>

                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Resources Designation Table - Show when clicking on expenses in total column OR hours/parts for non-hardware projects */}
                                                        {showExpensesForProject === `project-${index}-${projectIndex}` && 
                                                            (highlightedSection === `project-${index}-${projectIndex}-expenses` || 
                                                            (highlightedSection === `project-${index}-${projectIndex}-hours` && !project?.TypeService?.toLowerCase().includes("hardware"))) && (
                                                            <div className="w-full">
                                                                <QuotedExpenseTable
                                                                    expenseTableData={expenseTableData}
                                                                    employeeData={employees}
                                                                    perdiem={perdiem}
                                                                    averagecost={averagecost}
                                                                    preserveDecimalHourlyRates={true} />
                                                            </div>
                                                        )}

                                                        {/* Part List Table with Tabs - Show when clicking on hours/parts in total column for hardware projects only */}
                                                        {project?.TypeService?.toLowerCase().includes("hardware") && 
                                                            showExpensesForProject === `project-${index}-${projectIndex}` && 
                                                            highlightedSection === `project-${index}-${projectIndex}-hours` && (
                                                            <>
                                                                <div className={`p-1  flex ${operationclicked ? "justify-center" : "justify-end"} gap-2`}>
                                                                    <div
                                                                        onClick={() =>
                                                                            setShowActionToAssign((prev) => ({
                                                                                ...prev,
                                                                                [index]: "tasktable",
                                                                            }))
                                                                        }
                                                                        className={`cursor-pointer py-2 px-4 inline-block border rounded-md text-start ${showActionToAssign[index] === "tasktable"
                                                                            ? "bg-blue-500 text-white"
                                                                            : "bg-white"
                                                                            }`}
                                                                    >
                                                                        {project?.TypeService?.toLowerCase().includes("hardware")
                                                                            ? "Part List"
                                                                            : "Effort Expenses"}
                                                                    </div>
                                                                    <div
                                                                        onClick={() =>
                                                                            setShowActionToAssign((prev) => ({
                                                                                ...prev,
                                                                                [index]: "otherexpenses",
                                                                            }))
                                                                        }
                                                                        className={`cursor-pointer py-2 px-4 inline-block border rounded-md text-start ${showActionToAssign[index] ===
                                                                            "otherexpenses"
                                                                            ? "bg-blue-500 text-white"
                                                                            : "bg-white"
                                                                            }`}
                                                                    >
                                                                        Other Expenses
                                                                    </div>
                                                                </div>

                                                                {showActionToAssign?.[index] ===
                                                                    "tasktable" && (
                                                                        <div className={`flex  ${operationclicked ? "justify-center" : "justify-end"}`}>
                                                                            <div className="w-[50%]">
                                                                                <div className="head flex text-center bg-[#E8E8E8]  text-black text-xs">
                                                                                    <div className="w-[30%] border-l p-1">
                                                                                        Parts
                                                                                    </div>
                                                                                    <div className="w-[20%] p-1">
                                                                                        Quantity
                                                                                    </div>
                                                                                    <div className="w-[20%] p-1">
                                                                                        Unit Cost ({currency})
                                                                                    </div>
                                                                                    <div className="w-[30%] border-r p-1">
                                                                                        Total Cost ({currency})
                                                                                    </div>
                                                                                </div>
                                                                                {/* Total Row - Below header, above parts */}
                                                                                <div className="flex text-center border-b">
                                                                                    <div className="w-[30%] p-1 border-l text-center">
                                                                                    </div>
                                                                                    <div className="w-[20%] p-1 text-center">
                                                                                    </div>
                                                                                    <div className="w-[20%] p-1 text-center">
                                                                                    </div>
                                                                                    <div className="w-[30%] p-1 border-r text-center text-xs font-bold">
                                                                                        {USAComma(
                                                                                            (() => {
                                                                                                const deduped = Object.values(
                                                                                                    (hardwareEntries || []).reduce((acc, item) => {
                                                                                                        const key = item.assemblepartdescription?.toLowerCase();
                                                                                                        if (!key) return acc;
                                                                                                        if (!acc[key] || Number(item.quantity) > Number(acc[key].quantity)) {
                                                                                                            acc[key] = item;
                                                                                                        }
                                                                                                        return acc;
                                                                                                    }, {})
                                                                                                );
                                                                                                return deduped.reduce((sum, item) => sum + (Number(item.totalprice) || 0), 0);
                                                                                            })()
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                                {hardwareEntries.map((entry, entryIndex) => (
                                                                                    <div
                                                                                        key={entryIndex}
                                                                                        className="head flex text-center"
                                                                                    >
                                                                                        <div className="w-[30%] border-b border-l p-1">
                                                                                            {entry.assemblepartdescription ||
                                                                                                ""}
                                                                                        </div>
                                                                                        <div className="w-[20%] border-b p-1">
                                                                                            {entry.quantity || " "}
                                                                                        </div>
                                                                                        <div className="w-[20%] border-b p-1">
                                                                                            {USAComma(entry.unitprice || " ")}
                                                                                        </div>
                                                                                        <div className="w-[30%] border-b border-r p-1">
                                                                                            {USAComma(entry.totalprice || " ")}
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                {showActionToAssign?.[index] ===
                                                                    "otherexpenses" && (
                                                                        <div className={`flex ${operationclicked ? "justify-center" : "justify-end"}`}>
                                                                            <div className="w-[50%]">
                                                                                <div className="head flex text-center bg-[#E8E8E8]  text-black text-xs">
                                                                                    <div className="w-[10%] border p-1">
                                                                                        Sr. No
                                                                                    </div>
                                                                                    <div className="w-[40%] border p-1">
                                                                                        Description
                                                                                    </div>
                                                                                    <div className="w-[20%] border p-1">
                                                                                        Amount ({currency})
                                                                                    </div>
                                                                                    <div className="w-[30%] border p-1">
                                                                                        Notes
                                                                                    </div>
                                                                                </div>
                                                                                <div className="head flex text-center">
                                                                                    <div className="w-[10%] border p-1"></div>
                                                                                    <div className="w-[40%] border p-1">
                                                                                        Total
                                                                                    </div>
                                                                                    <div className="w-[20%] border p-1">
                                                                                        {formatAmountComma(
                                                                                            managerDescriptions?.reduce(
                                                                                                (sum, item) => sum + item.value,
                                                                                                0
                                                                                            ) || 0
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="w-[30%] border p-1"></div>
                                                                                </div>

                                                                                {managerDescriptions.map(
                                                                                    (entry, entryIndex) => (
                                                                                        <div
                                                                                            key={entryIndex}
                                                                                            className="head flex text-center"
                                                                                        >
                                                                                            <div className="w-[10%] border p-1">
                                                                                                {entryIndex + 1}
                                                                                            </div>
                                                                                            <div className="w-[40%] border p-1">
                                                                                                {entry.description || " "}
                                                                                            </div>
                                                                                            <div className="w-[20%] border p-1">
                                                                                                {formatAmountComma(entry.value || " ")}
                                                                                            </div>
                                                                                            <div className="w-[30%] border p-1">
                                                                                                {entry.notes || " "}
                                                                                            </div>
                                                                                        </div>
                                                                                    )
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                            </>
                                                        )}

                                                        {/* Part List Table with Tabs - Show when clicking on manager/employee rows */}
                                                        {project?.TypeService?.toLowerCase().includes("hardware")
                                                            ? (activeTaskKey ===
                                                                `manager_${index}_${projectIndex}` ||
                                                                activeTaskKey?.startsWith(
                                                                    `operation_${index}_${projectIndex}_`
                                                                )) && (
                                                                <>
                                                                    <div className={`p-1  flex ${operationclicked ? "justify-center" : "justify-start"} gap-2`}>
                                                                        <div
                                                                            onClick={() =>
                                                                                setShowActionToAssign((prev) => ({
                                                                                    ...prev,
                                                                                    [index]: "tasktable",
                                                                                }))
                                                                            }
                                                                            className={`cursor-pointer py-2 px-4 inline-block border rounded-md text-start ${showActionToAssign[index] === "tasktable"
                                                                                ? "bg-blue-500 text-white"
                                                                                : "bg-white"
                                                                                }`}
                                                                        >
                                                                            {project?.TypeService?.toLowerCase().includes("hardware")
                                                                                ? "Part List"
                                                                                : "Effort Expenses"}

                                                                        </div>
                                                                        <div
                                                                            onClick={() =>
                                                                                setShowActionToAssign((prev) => ({
                                                                                    ...prev,
                                                                                    [index]: "otherexpenses",
                                                                                }))
                                                                            }
                                                                            className={`cursor-pointer py-2 px-4 inline-block border rounded-md text-start ${showActionToAssign[index] ===
                                                                                "otherexpenses"
                                                                                ? "bg-blue-500 text-white"
                                                                                : "bg-white"
                                                                                }`}
                                                                        >
                                                                            Other Expenses
                                                                        </div>
                                                                    </div>



                                                                    {showActionToAssign?.[index] ===
                                                                        "tasktable" && (
                                                                            <div className={`flex  ${operationclicked ? "justify-center" : "justify-start"}`}>
                                                                                <div className="w-[50%]">
                                                                                    <div className="head flex text-center bg-[#E8E8E8]  text-black text-xs">
                                                                                        <div className="w-[30%] border-l p-1">
                                                                                            Parts
                                                                                        </div>
                                                                                        <div className="w-[20%] p-1">
                                                                                            Quantity
                                                                                        </div>
                                                                                        <div className="w-[20%] p-1">
                                                                                            Unit Cost ({currency})
                                                                                        </div>
                                                                                        <div className="w-[30%] border-r p-1">
                                                                                            Total Cost ({currency})
                                                                                        </div>
                                                                                    </div>
                                                                                    {/* Total Row - Below header, above parts */}
                                                                                    <div className="flex text-center border-b">
                                                                                        <div className="w-[30%] p-1 border-l text-center">
                                                                                        </div>
                                                                                        <div className="w-[20%] p-1 text-center">
                                                                                        </div>
                                                                                        <div className="w-[20%] p-1 text-center">
                                                                                        </div>
                                                                                        <div className="w-[30%] p-1 border-r text-center text-xs font-bold">
                                                                                            {USAComma(
                                                                                                (() => {
                                                                                                    const deduped = Object.values(
                                                                                                        (hardwareEntries || []).reduce((acc, item) => {
                                                                                                            const key = item.assemblepartdescription?.toLowerCase();
                                                                                                            if (!key) return acc;
                                                                                                            if (!acc[key] || Number(item.quantity) > Number(acc[key].quantity)) {
                                                                                                                acc[key] = item;
                                                                                                            }
                                                                                                            return acc;
                                                                                                        }, {})
                                                                                                    );
                                                                                                    return deduped.reduce((sum, item) => sum + (Number(item.totalprice) || 0), 0);
                                                                                                })()
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                    {hardwareEntries.map((entry, index) => (
                                                                                        <div
                                                                                            key={index}
                                                                                            className="head flex text-center"
                                                                                        >
                                                                                            <div className="w-[30%] border-b border-l p-1">
                                                                                                {entry.assemblepartdescription ||
                                                                                                    ""}
                                                                                            </div>
                                                                                            <div className="w-[20%] border-b p-1">
                                                                                                {entry.quantity || " "}
                                                                                            </div>
                                                                                            <div className="w-[20%] border-b p-1">
                                                                                                {USAComma(entry.unitprice || " ")}
                                                                                            </div>
                                                                                            <div className="w-[30%] border-b border-r p-1">
                                                                                                {USAComma(entry.totalprice || " ")}
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                    {showActionToAssign?.[index] ===
                                                                        "otherexpenses" && (
                                                                            <div className={`flex ${operationclicked ? "justify-center" : "justify-start"}`}>
                                                                                <div className="w-[50%]">
                                                                                    <div className="head flex text-center bg-[#E8E8E8]  text-black text-xs">
                                                                                        <div className="w-[10%] border p-1">
                                                                                            Sr. No
                                                                                        </div>
                                                                                        <div className="w-[40%] border p-1">
                                                                                            Description
                                                                                        </div>
                                                                                        <div className="w-[20%] border p-1">
                                                                                            Amount ({currency})
                                                                                        </div>
                                                                                        <div className="w-[30%] border p-1">
                                                                                            Notes
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="head flex text-center">
                                                                                        <div className="w-[10%] border p-1"></div>
                                                                                        <div className="w-[40%] border p-1">
                                                                                            Total
                                                                                        </div>
                                                                                        <div className="w-[20%] border p-1">
                                                                                            {formatAmountComma(
                                                                                                managerDescriptions?.reduce(
                                                                                                    (sum, item) => sum + item.value,
                                                                                                    0
                                                                                                ) || 0
                                                                                            )}
                                                                                        </div>
                                                                                        <div className="w-[30%] border p-1"></div>
                                                                                    </div>

                                                                                    {managerDescriptions.map(
                                                                                        (entry, index) => (
                                                                                            <div
                                                                                                key={index}
                                                                                                className="head flex text-center"
                                                                                            >
                                                                                                <div className="w-[10%] border p-1">
                                                                                                    {index + 1}
                                                                                                </div>
                                                                                                <div className="w-[40%] border p-1">
                                                                                                    {entry.description}
                                                                                                </div>
                                                                                                <div className="w-[20%] border p-1">
                                                                                                    {formatAmountComma(entry.value)}
                                                                                                </div>
                                                                                                <div className="w-[30%] border p-1">
                                                                                                    {entry.notes}
                                                                                                </div>
                                                                                            </div>
                                                                                        )
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                </>
                                                            )
                                                            : (activeTaskKey ===
                                                                `manager_${index}_${projectIndex}` ||
                                                                activeTaskKey?.startsWith(
                                                                    `operation_${index}_${projectIndex}_`
                                                                )) &&
                                                            ((Entries.length > 0 || managerDescriptions.length > 0) ? (
                                                                <>
                                                                    <div className={`p-1 flex  ${operationclicked ? "justify-center" : "justify-start"} gap-2`}>
                                                                        <div
                                                                            onClick={() =>
                                                                                setShowActionToAssign((prev) => ({
                                                                                    ...prev,
                                                                                    [index]: "tasktable",
                                                                                }))
                                                                            }
                                                                            className={`cursor-pointer py-2 px-4 inline-block border rounded-md text-start ${showActionToAssign[index] === "tasktable"
                                                                                ? "bg-blue-500 text-white"
                                                                                : "bg-white"
                                                                                }`}
                                                                        >
                                                                            {/* {project.TypeService === "Hardware"
                                      ? "Part List"
                                      : "Effort Expenses"} */}
                                                                            {project?.TypeService?.toLowerCase().includes("hardware")
                                                                                ? "Part List"
                                                                                : "Effort Expenses"}

                                                                        </div>
                                                                        <div
                                                                            onClick={() =>
                                                                                setShowActionToAssign((prev) => ({
                                                                                    ...prev,
                                                                                    [index]: "otherexpenses",
                                                                                }))
                                                                            }
                                                                            className={`cursor-pointer py-2 px-4 inline-block border rounded-md text-start ${showActionToAssign[index] ===
                                                                                "otherexpenses"
                                                                                ? "bg-blue-500 text-white"
                                                                                : "bg-white"
                                                                                }`}
                                                                        >
                                                                            Other Expenses
                                                                        </div>
                                                                    </div>

                                                                    {showActionToAssign?.[index] ===
                                                                        "tasktable" && (
                                                                            <div className={`flex ${operationclicked ? "justify-center" : "justify-start"}  w-full`}>
                                                                                <div className="w-[50%] max-h-[14rem] text-end  overflow-scroll">
                                                                                    <div className="sticky top-0">
                                                                                        <div className="head flex text-center bg-[#E8E8E8]  text-black text-xs">

                                                                                            <div className="w-[40%] border p-1">
                                                                                                Task
                                                                                            </div>
                                                                                            <div className="w-[30%] border p-1">
                                                                                                Hours
                                                                                            </div>
                                                                                            <div className="w-[30%] border p-1">
                                                                                                Notes
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="head flex text-center bg-gray-100">
                                                                                            <div className="w-[40%] border p-1">
                                                                                                Total
                                                                                            </div>
                                                                                            <div className="w-[30%] border p-1">
                                                                                                {Entries?.reduce(
                                                                                                    (sum, item) =>
                                                                                                        sum + item.hoursassigned,
                                                                                                    0
                                                                                                ) || 0}
                                                                                            </div>
                                                                                            <div className="w-[30%] border p-1">
                                                                                                {Entries.length} Tasks
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>

                                                                                    {Entries.map((entry, index) => (
                                                                                        <div
                                                                                            key={index}
                                                                                            className="head flex text-center"
                                                                                        >

                                                                                            <div className="w-[40%] border p-1">
                                                                                                {entry.task}
                                                                                            </div>
                                                                                            <div className="w-[30%] border p-1">
                                                                                                {entry.hoursassigned}
                                                                                            </div>
                                                                                            <div className="w-[30%] border p-1">
                                                                                                {entry.notes}
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                    {showActionToAssign?.[index] ===
                                                                        "otherexpenses" && (
                                                                            <div className={`flex ${operationclicked ? "justify-center" : "justify-start"}   w-full`}>
                                                                                <div className="w-[50%] max-h-[14rem]  text-end  overflow-scroll">
                                                                                    <div className="sticky top-0">
                                                                                        <div className="head flex sticky top-0 text-center bg-[#E8E8E8]  text-black text-xs">

                                                                                            <div className="w-[40%] border p-1">
                                                                                                Description
                                                                                            </div>
                                                                                            <div className="w-[30%] border p-1">
                                                                                                Amount ({currency})
                                                                                            </div>
                                                                                            <div className="w-[30%] border p-1">
                                                                                                Notes
                                                                                            </div>
                                                                                        </div>

                                                                                        <div className="head flex text-center bg-gray-100">
                                                                                            <div className="w-[40%] border p-1">
                                                                                                {"Total"}
                                                                                            </div>
                                                                                            <div className="w-[30%] border p-1">
                                                                                                {formatAmountComma(
                                                                                                    managerDescriptions?.reduce(
                                                                                                        (sum, item) => sum + item.value,
                                                                                                        0
                                                                                                    ) || 0
                                                                                                )}
                                                                                            </div>
                                                                                            <div className="w-[30%] border p-1">{managerDescriptions.length} Expenses</div>
                                                                                        </div>
                                                                                    </div>

                                                                                    {managerDescriptions.map(
                                                                                        (entry, index) => (
                                                                                            <div
                                                                                                key={index}
                                                                                                className="head flex text-center"
                                                                                            >

                                                                                                <div className="w-[40%] border p-1">
                                                                                                    {entry.description}
                                                                                                </div>
                                                                                                <div className="w-[30%] border p-1">
                                                                                                    {formatAmountComma(entry.value)}
                                                                                                </div>
                                                                                                <div className="w-[30%] border p-1">
                                                                                                    {entry.notes}
                                                                                                </div>
                                                                                            </div>
                                                                                        )
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                </>
                                                            ) : (
                                                                <div className="text-center text-red-500 p-1">
                                                                    No Task Data Available
                                                                </div>
                                                            ))}
                                                    </>
                                                ))}
                                            </div>
                                        ) : (
                                            <div
                                                onClick={() => {
                                                    setrowid(prev => prev === getRowUniqueKey(row) ? null : getRowUniqueKey(row))
                                                    setSelected(!selected)
                                                }}
                                                className={` ${rowid ? "" : "w-[20%]"} border-b cursor-pointer border-y flex justify-center align-middle items-center`}>
                                                {rowid ? (
                                                    <div className='flex  group-hover:bg-gray-100'>
                                                        <div className="w-28 flex-shrink-0  p-2 py-4 ">
                                                        </div>
                                                        <div className="w-24 flex-shrink-0  p-2 py-4 break-words text-wrap flex justify-center align-middle items-center ">

                                                        </div>
                                                        <div className="w-32 flex-shrink-0  p-2 py-4 ">

                                                        </div>
                                                        <div className="w-28  flex-shrink-0 p-2 py-4 ">
                                                        </div>
                                                        <div className="w-24 flex-shrink-0  p-2 py-4 ">
                                                            Multiple
                                                        </div>
                                                        <div className="w-32 flex-shrink-0  p-2 py-4 ">

                                                        </div>
                                                        <div className="w-48 flex-shrink-0  p-2 py-4 ">

                                                        </div>
                                                        <div className="w-32 flex-shrink-0 p-2 py-4 ">

                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        Multiple
                                                    </div>
                                                )}
                                            </div>
                                        )}



                                    </div>
                                )
                            })}
                            {sortedProjects.length === 0 && (
                                <div className="noDataAvailable p-2">No Result Found</div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="w-[99%] flex justify-end">
                    <div className="flex gap-4 pt-2">
                        <button
                            onClick={() => {
                                // Preserve the status when going back to Existing Project
                                // This ensures that when clicking Next again, it goes to the correct page
                                dispatch(setOperationProjectStatus("response received"));
                                dispatch(setCreatedProject(createdProject));
                                dispatch(goToStep(1));
                            }}
                            className=" py-2 px-7 bg-black overflow-hidden shadow-md text-white rounded-lg"
                        >
                            Back
                        </button>
                        <button
                            onClick={() => {
                                if (isAllManagerAccepted) {
                                    handleNextRR()
                                }
                            }}
                            className={` py-2 px-7 ${isAllManagerAccepted ? "bg-[#e32278]  cursor-pointer" : "bg-[#fcadd0] cursor-default "} overflow-hidden shadow-md text-white rounded-lg`}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div >

        </>
    );
};

export default ResponseReceivedWizard;


